// Scripted AI opponent.
//
// The design goal is that a competent human cannot tell, from behaviour alone,
// that this is a script rather than a player on the other end of the network.
// That means six things, and every part of this file exists to serve one of
// them:
//
//   1. It plays a real build order on real timings. A two-barracks infantry
//      push leaves home at ~3:40 and ARRIVES at the enemy at ~4:10, because
//      that is when a human's would arrive. Gas is taken at ~2:20, which is
//      what pays for the factory at ~4:00 and tanks before seven minutes.
//   2. It never cheats. Every single intent -- build, train, move, siege --
//      goes out through world.issue(), the exact command path the mouse uses,
//      and is validated by commands.js the same way. Every read of the enemy
//      goes through fog.js: canSee() for units, world.fog[me].memory for
//      structures. The only thing it "knows" for free is the enemy start
//      location, which on a two-player map a human also knows.
//   3. It spends like a human, not like a queue. There is exactly one thing it
//      is saving for at any moment (structurePlan), and unit production only
//      ever sees the SURPLUS above that. Without this the AI dumps 100% of its
//      income into infantry, never banks the 400 for an expansion or the 200
//      for a factory, and quietly loses the macro game while looking busy.
//      Workers and supply depots are the two exemptions, because a human
//      saving for an expansion cuts marines, never SCVs and never depots.
//   4. It never queues a unit it does not have the supply to pop. Supply room
//      is checked against everything already in flight, not just against the
//      current number, so a finished unit never stands at the door. That one
//      rule is the whole difference between "supply blocked for 48 seconds"
//      and "supply blocked for none".
//   5. Its army moves as an army. Units mass at a staging point, advance along
//      a route in waypoint hops with a cohesion gate at each hop, and
//      reinforcements chase the group down and fold into it. Every group order
//      is issued to a STABLE tile centre, because movement.js buckets its
//      flow-field groups by goal tile: re-issuing to a goal that slides a
//      fraction of a tile each time splits one push into N groups of one and
//      turns a push back into a trickle.
//   6. It reacts to what it sees. A scouted third barracks before 3:00 buys an
//      extra barracks at home and delays the push; a scouted starport or a
//      single sighted flier buys missile turrets; scouted armour switches
//      infantry production off concussive bruisers and onto riflemen.
//
// STRUCTURE
//   observe()        -- turn earned vision into intel
//   macro()          -- supply, the single build goal, workers, gas, production
//   structurePlan()  -- what we are saving for, and the reserve held for it
//   scout()          -- one worker at ~1:05, then a loop around the enemy bases
//   army()           -- squad state machine: mass -> attack -> (retreat) -> mass,
//                       pre-empted by defend; plus tank siege micro and wraith
//                       harass
//
// This module owns NOTHING on the entity except the commands it issues. It
// mutates no entity fields directly; all bookkeeping lives on the `ai` object.

import { UnitDefs, BuildingDefs, ResearchDefs, SIZE } from './defs.js';
import { unavailableReason, techMet } from './commands.js';
import { canSee, isVisible } from './fog.js';
import { MAX_SUPPLY, FOG_VISIBLE, TICK_RATE } from './constants.js';

const AI_PERIOD = 6;              // decisions at 4 Hz -- roughly human reaction time
const MICRO_PERIOD = 3;           // siege/cloak toggles at 8 Hz

const NO_RESERVE = { minerals: 0, gas: 0, what: null };

// ---------------------------------------------------------------------------
// Difficulty presets. THREE PLAYERS, and not one of them cheats: every tier
// mines the same minerals at the same rate, sees only what its own fog shows
// it, and issues the same commands through the same queue. What differs is how
// well they are PLAYED --
//
//   standard    the default, and the one a first game is against. A decent
//               macro player with ordinary hands: two barracks instead of
//               three, no worker cut for tempo, the push at five minutes
//               instead of three-and-a-half, a smaller army committed to it,
//               a few units always left at home, no worker pull, tanks that
//               walk into range instead of setting up short of it, and a habit
//               of hitting whatever building is nearest rather than the one
//               that matters. Every one of those is a way a person plays, not
//               a number secretly divided by two -- and it still takes an
//               undefended base apart in under a minute, so a player who does
//               nothing still loses.
//   veteran     exactly the AI that beat six of six blind playtesters: the
//               three-rax timing attack that ARRIVES at 4:10, a factory at
//               5:00, the natural at 6:00 and siege tanks before seven
//               minutes. Unchanged, and still the ceiling.
//   aggressive  veteran's build order with the economy traded for tempo:
//               arrives at 3:50 on a thinner bank, expands minutes later,
//               and re-commits where veteran would regroup.
//
// world.js constructs its AI with 'standard' whenever nobody chose, which is
// what makes the first tier the one a first match is actually against.
// ---------------------------------------------------------------------------
export const DIFFICULTY = {
  standard: {
    // "Two rax, gas, expand": the push ARRIVES at about 5:00 -- late enough
    // that a player who macros gets to build something first -- and it comes
    // with ten supply rather than a maxed timing attack behind it.
    opener: [
      { at: 108, supply: 11, defId: 'barracks' },
      { at: 152, supply: 13, defId: 'refinery' },
      { at: 196, supply: 16, defId: 'barracks' },
    ],
    // A base and a half of workers, and it never cuts them for tempo: the
    // economy is fine, the ARMY is what arrives late.
    workersPerHall: 13, workersPerGas: 3, maxWorkers: 24,
    cutWorkersAt: 99, cutUntil: 0,
    maxRax: 3, maxFactories: 1, maxStarports: 1,
    scoutAt: 92,
    raxPerHall: 2, raxCadence: 96,
    firstAttackTime: 270, firstAttackValue: 10, firstAttackDeadline: 320,
    // Smaller waves than veteran's, and it does not wait as long between them:
    // it keeps turning up with an army you can beat rather than massing one you
    // cannot. An opponent that stops threatening you is not an easier opponent.
    waveValues: [10, 22, 34, 48, 64, 82, 100, 120],
    factoryTime: 310, starportTime: 620, secondGasTime: 470,
    expandTime: 290, thirdBaseTime: 660,
    armyFloorStart: 240, armyFloorSlope: 22, armyFloorMax: 16,
    // Breaks off a fight sooner and takes longer to come back, which is what
    // gives a player the window to counter-attack.
    retreatRatio: 0.6, commitSeconds: 9, regroupSeconds: 26,
    firstWaveRetreat: 0.65, firstWaveCommit: 8, firstWaveOdds: 0.85,
    bruiserRatio: 0.35,
    garrisonAt: 16, garrisonMax: 6,
    workerPull: false,          // never pulls SCVs to trade
    turretsPerBase: 1,
    siegePush: false,           // tanks walk into range instead of setting up short
    smartTargets: false,        // hits the nearest building, not the one that matters
    period: 12, microPeriod: 6, // decides at 2 Hz, not 4
    huntParties: 6, huntScouts: 3,
  },
  veteran: {
    // "Three rax, gas, factory, expand": the push ARRIVES at ~4:10, the factory
    // goes down at ~5:00, the natural at ~6:00, and the first siege tank is on
    // the field before seven minutes with Siege Tech behind it.
    // Supply depots are deliberately not in the opener -- supplyPlan owns those,
    // and pinning them to a step number only fights it.
    opener: [
      { at: 98,  supply: 10, defId: 'barracks' },
      { at: 126, supply: 11, defId: 'barracks' },
      { at: 156, supply: 13, defId: 'refinery' },
      { at: 188, supply: 15, defId: 'barracks' },
    ],
    workersPerHall: 16, workersPerGas: 3, maxWorkers: 34,
    // Tempo cut: a real timing attack stops making SCVs a couple of workers
    // short of saturation and turns that income into infantry, which is the ONLY
    // reason the attacker is ever ahead on a mirror map. Workers resume at
    // cutUntil, which is about when the push leaves.
    cutWorkersAt: 16, cutUntil: 196,
    maxRax: 6, maxFactories: 2, maxStarports: 1,
    scoutAt: 66,
    raxPerHall: 3, raxCadence: 62,
    firstAttackTime: 208, firstAttackValue: 7, firstAttackDeadline: 240,
    waveValues: [8, 30, 48, 66, 86, 108, 130, 152],
    factoryTime: 282, starportTime: 560, secondGasTime: 396,
    expandTime: 250, thirdBaseTime: 560,
    armyFloorStart: 200, armyFloorSlope: 17, armyFloorMax: 22,
    retreatRatio: 0.5, commitSeconds: 12, regroupSeconds: 18,
    firstWaveRetreat: 0.6, firstWaveCommit: 9, firstWaveOdds: 1.0,
    bruiserRatio: 0.45,         // fraction of infantry *count* that is bruisers
    garrisonAt: 22, garrisonMax: 4,
    workerPull: true,
    turretsPerBase: 2,
    siegePush: true, smartTargets: true,
    period: AI_PERIOD, microPeriod: MICRO_PERIOD,
    huntParties: 6, huntScouts: 3,
  },
  aggressive: {
    // "Three rax pressure": arrives at ~3:50 on a much thinner economy, takes
    // gas and the natural minutes later than standard does, fights on worse
    // margins and keeps re-committing where standard would regroup. The tech is
    // real, but it comes after the aggression rather than paying for it.
    opener: [
      { at: 92,  supply: 10, defId: 'barracks' },
      { at: 118, supply: 11, defId: 'barracks' },
      { at: 150, supply: 13, defId: 'barracks' },
      { at: 196, supply: 17, defId: 'refinery' },
    ],
    workersPerHall: 15, workersPerGas: 3, maxWorkers: 30,
    cutWorkersAt: 13, cutUntil: 192,
    maxRax: 7, maxFactories: 1, maxStarports: 1,
    scoutAt: 58,
    raxPerHall: 4, raxCadence: 52,
    firstAttackTime: 190, firstAttackValue: 6, firstAttackDeadline: 218,
    waveValues: [7, 22, 36, 52, 70, 90, 112, 134],
    factoryTime: 330, starportTime: 560, secondGasTime: 430,
    expandTime: 350, thirdBaseTime: 640,
    armyFloorStart: 175, armyFloorSlope: 13, armyFloorMax: 30,
    retreatRatio: 0.45, commitSeconds: 14, regroupSeconds: 12,
    firstWaveRetreat: 0.3, firstWaveCommit: 18, firstWaveOdds: 1.6,
    bruiserRatio: 0.6,
    garrisonAt: 34, garrisonMax: 2,
    workerPull: true,
    turretsPerBase: 1,
    siegePush: true, smartTargets: true,
    period: AI_PERIOD, microPeriod: MICRO_PERIOD,
    huntParties: 6, huntScouts: 3,
  },
};

export function createAI(world, player, difficulty = 'standard') {
  const cfg = DIFFICULTY[difficulty] ? DIFFICULTY[difficulty] : DIFFICULTY.standard;
  const home = { ...world.startLocations[player] };
  const enemyHome = { ...world.startLocations[1 - player] };

  // Our natural: the non-main base seeded for us by the map.
  const nat = world.bases.find((b) => b.owner === player && !b.isMain);
  const natural = nat ? { ...nat.cc } : { ...home };

  // Staging point: on the path out of the main, in front of the natural. This
  // is where the army lives when it is not attacking -- close enough to defend
  // both bases, far enough forward that a push does not start from scratch.
  const dx = enemyHome.x - home.x, dy = enemyHome.y - home.y;
  const len = Math.hypot(dx, dy) || 1;

  return {
    player, cfg, difficulty,
    home, enemyHome, natural,
    staging: { x: home.x + (dx / len) * 14, y: home.y + (dy / len) * 14 },
    opener: cfg.opener.map((s) => ({ ...s })),

    // Economy triage. `peakWorkers` is what tells a wiped-out economy apart from
    // an opening: four workers at 0:10 is a build order, four workers at 8:10 is
    // an emergency. `floor` is minerals held out of every other budget so the
    // AI can never be stranded below the 50 a rebuild costs.
    peakWorkers: 0, emergency: 0, floor: 0,
    // Home defence. `holdRamp` latches when the fight at home is one we lose in
    // the open, and moves the whole army onto the high ground behind the ramp.
    holdRamp: false, rampPoint: null, holdPoint: null,
    lastThreatValue: 0, lastThreatTick: -1e6, believed: 0, evacTick: -1e6,

    pending: [],                 // { defId, tick } -- commands issued but not yet applied
    // Money already committed by commands issued THIS tick. world.issue() is
    // deferred, so everything decided in one pass lands together at the top of
    // the next tick: without this, a supply depot decided two lines above an
    // expansion both see the same 450 minerals, the depot is applied first, and
    // the 400-mineral command centre is silently rejected for "not enough
    // minerals" after the planner has already congratulated itself on placing
    // it. The symptom is an AI that decides to expand at 4:20 and expands at
    // 6:46, having re-decided it forty times in between.
    spent: { minerals: 0, gas: 0 },
    reserve: NO_RESERVE,         // minerals/gas held back for `goal`
    goal: null,                  // defId of the structure/research we are saving for
    scoutId: null, scoutSent: false, scoutLeg: 0, scoutDeadTick: -1e6,

    squad: {
      state: 'mass',             // mass | attack | retreat | defend
      ids: new Set(),
      wave: 0,
      target: null,              // { x, y }
      route: null, wpIdx: 0, wpSince: 0,
      startValue: 0, peakValue: 0, sieging: false,
      // Stand-off bookkeeping. `standoffSince` is when the tanks last set up
      // short of the objective; `pushIn` latches when that stand-off has held
      // with nothing in sight, which means the thing we are besieging is a
      // memory and the only way to learn that is to walk onto it.
      standoffSince: -1, pushIn: false,
      stateSince: 0,
      lastWaveTick: -9999,
      threat: null,
      launchTick: -1, arrivedTick: -1,   // diagnostics: when wave 0 left / landed
    },
    garrison: new Set(),         // army units held at home once we are on two bases
    // Search parties, formed only while mopping up. See doHunt().
    hunt: { parties: [], scouts: new Set() },
    harass: { ids: new Set(), state: 'idle', since: 0 },
    defenders: new Set(),        // worker ids pulled to fight

    intel: {
      enemyRax: 0, enemyFactory: false, enemyStarport: false,
      enemyHalls: 1, enemyTurrets: 0,
      visibleArmy: 0, enemyArmyEst: 0, airThreat: false, armourThreat: false, rushSuspected: false,
      lastContactTick: -9999,
    },

    armyValue: 0,
    expandCommit: false,
    // True once we have ever had an enemy structure in fog memory. It is the
    // difference between "we have not looked yet" (opening) and "we have
    // razed everything we ever found" (go and finish it).
    sawEnemyStructures: false,
    sweep: null, targetKind: 'memory',
    gp: {},                      // stable group-order goal tiles, keyed by purpose
    routeCache: new Map(),
  };
}

export function aiSystem(world) {
  const ai = world.ai;
  if (!ai) return;
  runAI(world, ai);
}

/** Drive one AI brain. Exported so a second brain can be attached in tests. */
export function runAI(world, ai) {
  const p = world.players[ai.player];
  if (!p || p.defeated || world.gameOver) return;

  // How fast this player's hands are. The default tier thinks at 2 Hz and the
  // two above it at 4 Hz, which is a real and entirely fair difference: every
  // reaction in the file -- noticing a raid, re-targeting a push, moving a
  // worker across to the new base, un-sieging a tank the army has walked off
  // and left -- happens half a second later. Both are inside human range; the
  // periods stay multiples of AI_PERIOD so the sub-cadences below (rally,
  // mining balance, objective refresh) still land on decision ticks.
  if (world.tick % (ai.cfg.microPeriod || MICRO_PERIOD) === 0) tankMicro(world, ai, p);
  if (world.tick % (ai.cfg.period || AI_PERIOD) !== 0) return;

  ai.spent.minerals = 0;
  ai.spent.gas = 0;
  expirePending(world, ai);
  observe(world, ai, p);
  macro(world, ai, p);
  scout(world, ai);
  army(world, ai, p);
}

// ===========================================================================
// Stable group goals.
//
// movement.js buckets units into flow-field groups by the INTEGER TILE of their
// order's goal, and only applies its cohesion throttle to groups of five or
// more attack-movers. An order re-issued to a goal that drifts half a tile
// therefore silently shatters one twenty-unit push into twenty groups of one,
// each free to run ahead and die alone. Every group order in this file goes
// through here: snapped to a tile centre, and held there until the thing we are
// aiming at has moved far enough to be worth a new order.
// ===========================================================================
function stablePoint(ai, key, x, y, tol) {
  const px = Math.floor(x) + 0.5, py = Math.floor(y) + 0.5;
  const cur = ai.gp[key];
  if (cur && Math.hypot(cur.x - px, cur.y - py) <= tol) return cur;
  const pt = { x: px, y: py };
  ai.gp[key] = pt;
  return pt;
}
function clearPoint(ai, key) { delete ai.gp[key]; }

// ===========================================================================
// Intel -- everything below is derived only from vision we actually earned.
// ===========================================================================
function observe(world, ai, p) {
  const foe = 1 - ai.player;
  const intel = ai.intel;

  let rax = 0, fac = 0, star = 0, halls = 0, turrets = 0;
  for (const m of world.fog[ai.player].memory.values()) {
    if (m.owner !== foe) continue;
    ai.sawEnemyStructures = true;
    switch (m.defId) {
      case 'barracks': rax++; break;
      case 'factory': fac++; break;
      case 'starport': star++; break;
      case 'command_center': halls++; break;
      case 'turret': turrets++; break;
      default: break;
    }
  }
  intel.enemyRax = Math.max(intel.enemyRax, rax);
  intel.enemyFactory = intel.enemyFactory || fac > 0;
  intel.enemyStarport = intel.enemyStarport || star > 0;
  intel.enemyHalls = Math.max(intel.enemyHalls, halls);
  intel.enemyTurrets = turrets;

  let vis = 0, air = 0, large = 0, closest = Infinity;
  for (const u of world.unitsOf(foe)) {
    const d = UnitDefs[u.defId];
    if (d.isWorker) continue;
    if (!canSee(world, ai.player, u)) continue;
    vis += d.supply;
    if (d.flying) air += d.supply;
    if (d.size === SIZE.LARGE && !d.flying) large += d.supply;
    closest = Math.min(closest, Math.hypot(u.x - ai.home.x, u.y - ai.home.y));
  }
  intel.visibleArmy = vis;
  // What we believe the enemy army is worth.
  //
  // Three sources, and the maximum of them wins. What we can see right now.
  // What we last saw, decayed with a ~2 minute half-life, because a human who
  // watched twenty marines walk past a minute ago does not then attack as if
  // the map were empty. And an inference from the production we SCOUTED: two
  // barracks and a factory are worth about a round of units standing in front
  // of them, whether or not anybody is currently looking at them.
  //
  // That last one is arithmetic on earned intel, not vision. It is also the
  // difference between an AI that walks seventeen supply into twenty-five
  // every ninety seconds because nothing was on screen, and one that waits.
  const bays = Math.max(1, intel.enemyRax + (intel.enemyFactory ? 1 : 0) + (intel.enemyStarport ? 1 : 0));
  const fromCapacity = bays * 4.5;
  intel.enemyArmyEst = Math.max(vis, (intel.enemyArmyEst || 0) * 0.9986, fromCapacity);
  if (air > 0 || intel.enemyStarport) intel.airThreat = true;
  // Concussive damage does a quarter of its number to LARGE units, so armour on
  // the field is the signal to stop making bruisers, not a footnote.
  if (large > 0 || intel.enemyFactory) intel.armourThreat = true;
  if (vis > 0) intel.lastContactTick = world.tick;

  // Rush read: three barracks scouted before any macro build would have them,
  // or an army on our doorstep before one could have walked there honestly.
  if (world.time < 180 && intel.enemyRax >= 3) intel.rushSuspected = true;
  if (world.time < 230 && closest < 34 && vis >= 5) intel.rushSuspected = true;
}

// ===========================================================================
// Macro
// ===========================================================================
function macro(world, ai, p) {
  const myUnits = world.unitsOf(ai.player);
  const workers = myUnits.filter((u) => UnitDefs[u.defId].isWorker);
  const halls = world.buildingsOf(ai.player).filter((b) => BuildingDefs[b.defId].isTownHall && !b.constructing);
  const t = world.time;
  ai.armyValue = sumValue(armyUnits(world, ai));

  // Triage before anything else: a dead economy outranks every other goal.
  ai.emergency = economyEmergency(world, ai, workers, halls);
  // The last 50 minerals are never army. Once there is an economy to lose, one
  // worker's worth of minerals is held out of unit production for the whole
  // game -- it costs a rifleman and it is the difference between "my base was
  // just run over" and "my base was just run over and I can never afford the
  // 50-mineral worker that digs me out of it, so this game has no ending".
  ai.floor = (halls.length && ai.peakWorkers >= 8) ? UnitDefs.worker.cost.minerals : 0;

  // Supply comes before the reserve: being blocked costs more than anything
  // the reserve is saving for, so a depot we want but cannot pay for is added
  // ON TOP of whatever the build goal is holding.
  const wantDepot = supplyPlan(world, ai, p, halls);
  structurePlan(world, ai, p, t, halls);
  if (wantDepot) {
    ai.reserve = { minerals: ai.reserve.minerals + 100, gas: ai.reserve.gas, what: ai.reserve.what || 'depot' };
  }

  const room = { supply: supplyRoom(world, ai, p) };
  workerPlan(world, ai, p, workers, halls, room);
  gasPlan(world, ai, p, workers);
  balanceMining(world, ai, workers, halls);
  productionBuildings(world, ai, p, t, halls);
  trainArmy(world, ai, p, room);
}

/**
 * How badly the economy is hurt, on a scale nothing else in this file gets to
 * argue with.
 *
 *   0  fine
 *   1  bleeding -- half the workers are gone. Stop buying capacity, and never
 *      spend the last 50 minerals on anything but a worker.
 *   2  the economy is DEAD. Everything stops: no army, no structures, no tech.
 *      Every town hall makes workers until there is an economy again.
 *
 * `peakWorkers` is what makes this safe to state in absolute numbers. Four
 * workers at 0:10 is the opening; four workers at 8:10 is a base that has just
 * been run over, and the two have to be told apart before "fewer than four
 * workers" can mean anything.
 *
 * The rule this exists to enforce is the one the AI used to break in the most
 * embarrassing way available: it would spend down to five minerals, lose its
 * last worker, and then sit on one command centre with no income and no way to
 * ever afford the 50-mineral worker that digs it out -- alive, unable to act,
 * and unable to lose, for the twelve remaining minutes of a game it had already
 * lost. A game has to end in a decision.
 */
function economyEmergency(world, ai, workers, halls) {
  ai.peakWorkers = Math.max(ai.peakWorkers, workers.length);
  if (!halls.length) return 0;
  // Distinguish "we have not built the economy yet" from "we had one".
  if (ai.peakWorkers < 8 && world.time < 150) return 0;
  if (workers.length < 4) return 2;
  if (workers.length < 8) return 1;
  return 0;
}

/** Minerals/gas available to unit production after the current build goal. */
function surplus(ai, p) {
  return {
    minerals: p.minerals - ai.spent.minerals - ai.reserve.minerals - ai.floor,
    gas: p.gas - ai.spent.gas - ai.reserve.gas,
  };
}

/** Can we pay for this on top of everything already ordered this tick? */
function affordNow(ai, p, cost) {
  return p.minerals - ai.spent.minerals >= (cost.minerals || 0)
      && p.gas - ai.spent.gas >= (cost.gas || 0);
}
function commit(ai, cost) {
  ai.spent.minerals += cost.minerals || 0;
  ai.spent.gas += cost.gas || 0;
}

// ---------------------------------------------------------------------------
// Supply.
//
// Two independent mechanisms, and both are needed:
//
//   supplyRoom() is the hard gate. A unit is only ever QUEUED if the supply to
//   pop it is already free after everything else in flight is counted. That is
//   what makes "supply blocked" a number near zero rather than a number near a
//   minute: a finished unit never waits at the door because it was never
//   started without a place to stand.
//
//   supplyPlan() is the pump. It builds depots ahead of demand, scaled by how
//   fast we can SPEND supply -- six barracks and a factory burn ~15 supply in
//   the 25 s a depot takes to go up -- so the hard gate above almost never
//   actually bites.
// ---------------------------------------------------------------------------
function supplyRoom(world, ai, p) {
  let inFlight = 0;
  for (const b of world.buildingsOf(ai.player)) {
    for (const q of b.queue) if (q.kind === 'unit') inFlight += UnitDefs[q.defId].supply;
  }
  return p.supplyMax - p.supplyUsed - inFlight;
}

function supplyPlan(world, ai, p, halls) {
  if (p.supplyMax >= MAX_SUPPLY) return false;
  const inFlight = planned(world, ai, 'depot');
  // Structures still going up count: they will be draining supply long before
  // the depot we are deciding on now finishes.
  const bays = world.buildingsOf(ai.player)
    .filter((b) => BuildingDefs[b.defId].produces.length && !BuildingDefs[b.defId].isTownHall).length;
  const opening = p.supplyMax <= 10 && world.time < 110;
  const need = opening ? 3 : 4 + bays * 2.6 + halls.length * 1.6;
  const room = supplyRoom(world, ai, p);
  // While the economy is being rebuilt, 100 minerals is two workers, and a
  // depot bought AHEAD of demand out of a bank of 105 is exactly how the AI
  // used to end up at five minerals with no workers and no way back. Only pay
  // for one once the block is actually here.
  if (ai.emergency >= 1 && room > 2) return false;
  const headroom = room + inFlight * 8;
  if (headroom >= need) return false;
  if (inFlight >= 4) return false;
  if (p.minerals >= 100 && tryBuild(world, ai, 'depot', { urgent: room < 1 })) return false;
  // Only claim a reserve when the block is imminent -- reserving 100 for every
  // depot the planner would merely *like* pushes the whole opening back.
  return room <= 3;
}

// ---------------------------------------------------------------------------
// The single build goal.
//
// A human is only ever saving for ONE thing. Modelling that explicitly is what
// stops the AI from converting 100% of its income into infantry and then
// wondering why it never got an expansion, a factory or siege tech. The goal is
// executed the instant it is affordable; while it is not, its cost is reserved
// and unit production only sees what is left over.
//
// Extra BARRACKS are deliberately not on this list. They are capacity, not a
// milestone, and a capacity purchase that outranks the expansion behind it will
// starve that expansion forever -- ask the previous version of this file, which
// spent every game from six minutes on "saving for" a barracks it could always
// afford and therefore never once saved for a town hall. productionBuildings()
// buys them out of surplus instead.
// ---------------------------------------------------------------------------
function structurePlan(world, ai, p, t, halls) {
  ai.reserve = NO_RESERVE;
  // Nothing on the build order outranks having an economy at all. A factory
  // bought with the minerals that were going to be the workers is a factory
  // with nothing to feed it.
  if (ai.emergency >= 1) { ai.goal = null; return; }
  const goal = nextStructure(world, ai, p, t, halls);
  ai.goal = goal ? goal.defId : null;
  if (!goal) return;

  if (affordNow(ai, p, goal.cost) && execGoal(world, ai, goal)) return;

  // Never bank while the army is under the floor. A structure bought with the
  // riflemen that were supposed to be holding the ramp is a structure the enemy
  // walks past on its way to the town hall -- which is exactly how an AI that
  // "expands at five minutes" ends up dead at five thirty with a half-built
  // command centre. Production is unblocked until the army catches up, and the
  // bank resumes filling the moment it does.
  if (ai.armyValue < armyFloor(ai, t)) return;

  // Do not sit on minerals for something whose gas half is still far away --
  // that stalls the army for a minute to buy a factory a few seconds sooner.
  if ((goal.cost.gas || 0) > 0 && p.gas < goal.cost.gas * 0.55) return;
  // And never bank through a fight at home. Units now beat a building later.
  const threat = ai.squad.threat;
  if (threat && (threat.value >= 8 || threat.hittingBuildings)) return;

  ai.reserve = { minerals: goal.cost.minerals || 0, gas: goal.cost.gas || 0, what: goal.defId };
}

/**
 * How much army the AI insists on having before it will save for anything. It
 * ramps with the clock -- an eight-supply defence is plenty at 3:30 and
 * suicidal at 8:00 -- and is floored by whatever enemy army it can actually
 * see, so a scouted attack always outranks the build order.
 */
function armyFloor(ai, t) {
  const cfg = ai.cfg;
  const byTime = Math.max(0, (t - cfg.armyFloorStart) / cfg.armyFloorSlope);
  // Against what we believe the ENEMY has, not against what is on screen. By
  // the time an enemy timing attack is visible from the mineral line it is four
  // seconds from it, and a factory, a town hall, a second refinery and Siege
  // Tech bought in the two minutes before it arrives are a thousand resources
  // that should have been riflemen.
  return Math.min(cfg.armyFloorMax, Math.max(byTime, ai.intel.enemyArmyEst * 0.85));
}

function execGoal(world, ai, goal) {
  let ok = false;
  if (goal.kind === 'research') ok = research(world, ai, goal.defId);
  else if (goal.defId === 'command_center') ok = placeExpansion(world, ai);
  else ok = tryBuild(world, ai, goal.defId, goal.opts);
  if (ok && goal.fromOpener) ai.opener.shift();
  if (ok && goal.defId === 'command_center') ai.expandCommit = false;
  return ok;
}

/**
 * The priority list. First entry whose preconditions hold is what we are saving
 * for. Order matters far more than the individual timings: the opening, then
 * anything the scout says is urgent, then tech, then the expansion, then
 * luxuries.
 */
function nextStructure(world, ai, p, t, halls) {
  const cfg = ai.cfg;
  const B = (defId, opts) => ({ defId, kind: 'build', cost: BuildingDefs[defId].cost, opts });
  const R = (defId) => ({ defId, kind: 'research', cost: ResearchDefs[defId].cost });
  const built = (id) => countBuilt(world, ai.player, id);
  const live = (id) => built(id) + planned(world, ai, id);
  const has = (id) => world.hasBuilding(ai.player, id);
  const canExpand = () => expansionWindow(world, ai, t) && nextBaseSite(world, ai);

  // 1. The scripted opening, in order, on supply or on the clock.
  const step = ai.opener[0];
  if (step && (p.supplyUsed >= step.supply || t >= step.at)) {
    if (step.defId !== 'refinery' || nearestFreeGeyser(world, ai)) {
      const g = B(step.defId); g.fromOpener = true; return g;
    }
    ai.opener.shift();          // no geyser to take; move on rather than stall
  }

  // 2. Anti-air the moment air is a real possibility. A wraith flight that
  //    arrives before the first turret does is a lost mineral line.
  if (ai.intel.airThreat && has('barracks')) {
    const want = cfg.turretsPerBase * Math.max(1, halls.length);
    if (live('turret') < want) return B('turret', { nearMineralLine: true });
  }

  // 2b. Rebuilding from nothing. A player who has just had their base run over
  //    does not need a second base, a factory or siege tech; they need somewhere
  //    to make a rifleman. Without this the priority list below happily banks
  //    400 for an expansion while the one thing standing is a command centre,
  //    which is how a recovery that was working stalled out at eleven workers,
  //    no army and no production for three minutes on seed 21.
  if (t > 240 && !ai.opener.length && has('depot') && live('barracks') < 1) return B('barracks');

  // 3. A refinery is the cheapest thing on this list and it gates everything
  //    below it, so a missing first refinery outranks all of it.
  if (t >= 120 && live('refinery') < 1 && nearestFreeGeyser(world, ai)) return B('refinery');

  // 4. Factory into tanks: the reason a mid-game push has teeth instead of
  //    being twenty riflemen walking into a defended ramp.
  //
  //    It goes in FRONT of the expansion, which looks backwards until you cost
  //    it: the factory is 200 and the town hall is 400, so banking the factory
  //    first delays the hall by about twenty seconds while pulling the first
  //    tank a full minute earlier. Banking the hall first does the reverse, and
  //    a minute is the difference between having tanks for the enemy's timing
  //    push and having them for the post-mortem.
  if (t >= cfg.factoryTime && has('barracks') && live('factory') < 1) return B('factory');

  // 5. The NATURAL. The backbone of the macro game -- and the reason the third
  //    base further down is NOT allowed to become a standing goal: an "expand"
  //    entry the AI can never satisfy (site contested, last one just died)
  //    makes every line below it dead code, and that is how a previous version
  //    of this file reached fifteen minutes with a thousand banked gas and no
  //    factory.
  if (t >= cfg.expandTime && live('command_center') < 2 && canExpand()) return B('command_center');

  // 6. Siege Tech, once the first tank is off the line. Researching it into an
  //    empty factory reserves 150/150 for a minute, which is exactly the minute
  //    the factory needed to build the tank the research is for.
  if (has('factory') && countUnits(world, ai.player, 'siege_tank') >= 1
      && !p.tech.has('siege_tech') && !researching(world, ai, 'siege_tech')) return R('siege_tech');

  // 7. Second refinery -- worth taking once there is a second base to take it
  //    at, and mandatory before a second factory can be fed.
  if (t >= cfg.secondGasTime && live('refinery') < 2 && nearestFreeGeyser(world, ai)) return B('refinery');

  // 8. Production capacity to match income. This is ranked HERE -- under the
  //    expansion and the tech, over the third base -- and it is bounded by a
  //    target count, which is the only reason it is safe to have on the list at
  //    all. An unbounded "want another barracks" entry above the expansion is
  //    a goal the AI can always afford and therefore always chooses, and every
  //    line below it becomes unreachable.
  if (has('depot') && !ai.opener.length && !gasBayHold(world, ai, p)
      && live('barracks') < raxTarget(world, ai, t, halls)) {
    return B('barracks');
  }

  // 9. Third base, behind the tech.
  if (t >= cfg.thirdBaseTime && live('command_center') < 3 && canExpand()) return B('command_center');

  // 10. Air. Wraiths are harassment, not a main army, so they are last -- and
  //    strictly after the factory has actually produced the tanks it was
  //    bought for. A starport that jumps the queue in front of the first two
  //    tanks is 150 minerals and 100 gas of nothing.
  if (t >= cfg.starportTime && has('factory') && countUnits(world, ai.player, 'siege_tank') >= 4
      && p.gas > 400 && live('starport') < cfg.maxStarports) return B('starport');
  if (has('starport') && p.tech.has('siege_tech') && p.gas > 350 && !p.tech.has('cloaking_field')
      && !researching(world, ai, 'cloaking_field')) return R('cloaking_field');

  // 11. A second factory once the gas can actually feed it.
  if (t > 430 && has('factory') && p.gas > 260 && countUnits(world, ai.player, 'siege_tank') >= 2
      && live('factory') < cfg.maxFactories) return B('factory');

  // 12. Late-game turret coverage even with no air scouted, because by then a
  //    dropped starport is a coin flip we do not want to lose.
  if (t > 660 && has('barracks') && live('turret') < halls.length) return B('turret', { nearMineralLine: true });

  return null;
}

/**
 * Banking 400 minerals is ~45 s of no unit production, so an expansion is not a
 * timer, it is a WINDOW: behind a push that is still alive, or with enough army
 * at home that the minute is survivable. And once the decision is taken it has
 * to stick -- re-deciding it four times a second banks 200 minerals, spends
 * them on riflemen, and starts over, which is how an AI ends up on one base at
 * ten minutes with nothing to show for it.
 */
function expansionWindow(world, ai, t) {
  const cfg = ai.cfg;
  const sq = ai.squad;
  if (sq.threat && sq.threat.hittingBuildings) { ai.expandCommit = false; return false; }
  // A commitment taken behind a push that has since been wiped is not a
  // commitment any more, it is a 400-mineral hole in the defence.
  if (ai.expandCommit && ai.armyValue < armyFloor(ai, t) * 0.5) { ai.expandCommit = false; return false; }
  if (ai.expandCommit) return true;
  if (t < cfg.expandTime) return false;
  // Late enough that a one-base AI is simply losing: take it regardless.
  if (t > cfg.expandTime + 90) { ai.expandCommit = true; return true; }
  const behindAPush = sq.state === 'attack';
  const holdingHome = ai.armyValue >= Math.max(8, ai.intel.visibleArmy * 1.2);
  if (!behindAPush && !holdingHome) return false;
  ai.expandCommit = true;
  return true;
}

function researching(world, ai, defId) {
  for (const b of world.buildingsOf(ai.player)) {
    for (const q of b.queue) if (q.kind === 'research' && q.defId === defId) return true;
  }
  return false;
}

function workerPlan(world, ai, p, workers, halls, room) {
  const cfg = ai.cfg;
  // The economy is gone. Nothing else is happening this tick anyway (see
  // economyEmergency), so every town hall goes on workers at once rather than
  // the usual one-per-decision trickle -- the recovery is a race against the
  // army that just took the base, and a hall standing idle during it is a hall
  // that will be rubble before its first worker pops.
  if (ai.emergency >= 2) {
    for (const h of halls) {
      if (h.queue.length >= 1) continue;
      if (room.supply < 1) break;
      if (train(world, ai, 'worker', h)) room.supply -= 1;
    }
    return;
  }
  // Workers are exempt from the reserve. They cost 50, they pay for themselves
  // in a minute, and a human saving for an expansion cuts marines, never SCVs.
  const refineries = world.buildingsOf(ai.player).filter((b) => b.defId === 'refinery' && !b.constructing).length;
  let target = Math.min(cfg.maxWorkers, halls.length * cfg.workersPerHall + refineries * cfg.workersPerGas);
  // ...with one exception, and it is the whole reason a timing attack works on
  // a mirror map: for the ~90 s before the push, income goes into infantry
  // instead of into more income. Skip the cut if the scout says we are the one
  // about to be attacked -- then the economy is not the thing at risk.
  if (world.time < cfg.cutUntil && !ai.intel.rushSuspected) {
    target = Math.min(target, cfg.cutWorkersAt);
  }
  const queued = halls.reduce((s, h) => s + h.queue.length, 0);
  if (workers.length + queued >= target) return;
  if (room.supply < 1) return;
  if (p.minerals < 50) return;
  // The one moment a human stops making SCVs: the town hall is nearly paid for.
  // Three workers' worth of minerals is fifteen seconds off the expansion, and
  // the expansion pays those three workers back inside a minute.
  if (ai.goal === 'command_center' && ai.reserve.minerals > 0
      && p.minerals >= ai.reserve.minerals * 0.55) return;
  for (const h of halls) {
    if (h.queue.length >= 1) continue;
    if (train(world, ai, 'worker', h)) { room.supply -= 1; return; }
  }
}

/**
 * Three workers per refinery, exactly like a human clicking them across.
 *
 * Gas is the single most-neglected resource for a scripted AI, because nothing
 * visibly breaks when a refinery is running on one worker -- the factory just
 * quietly arrives four minutes late and the siege tanks never happen at all.
 *
 * The rule that matters is the one that looks like a footnote: a worker already
 * assigned to a refinery is NEVER a candidate for another one. Without it, the
 * moment a second refinery finishes, the nearest full refinery's worker gets
 * re-ordered to the empty one; next tick the first refinery is one short so the
 * same worker is ordered back; and that worker spends the rest of the match
 * walking between two geysers at four hertz, mining neither. Both refineries
 * read as "manned" to anything counting orders, and the AI's gas income sits at
 * a third of what its two saturated refineries should be paying it.
 */
function gasPlan(world, ai, p, workers) {
  const cap = ai.cfg.workersPerGas;
  const refineries = [];
  for (const b of world.buildingsOf(ai.player)) if (b.defId === 'refinery' && !b.constructing) refineries.push(b);
  if (!refineries.length) return;

  // Which refinery, if any, is this worker already committed to? Both the node
  // it has latched onto and the order it is still walking in on count.
  const gasAssignment = (u) => {
    const n = u.gatherNode != null ? world.get(u.gatherNode) : null;
    if (n && n.defId === 'refinery') return n.id;
    if (u.order && u.order.type === 'gather' && u.order.targetId != null) {
      const t = world.get(u.order.targetId);
      if (t && t.defId === 'refinery') return t.id;
      if (t && t.defId === 'geyser' && t.refineryId != null) return t.refineryId;
    }
    return null;
  };

  const manned = new Map();
  let onMinerals = 0;
  for (const u of workers) {
    const id = gasAssignment(u);
    if (id != null) manned.set(id, (manned.get(id) || 0) + 1);
    else onMinerals++;
  }
  // Minerals first, always. A crippled base with three workers left will happily
  // put all three on gas and then sit at twenty-five minerals for the rest of
  // the match, mining a resource it has nothing left to spend on and unable to
  // afford the 50-mineral worker that would dig it out.
  if (onMinerals < 4) return;

  for (const r of refineries) {
    if ((manned.get(r.id) || 0) >= cap) continue;
    // Gas is a luxury when we are mineral-starved and swimming in vespene.
    if (p.gas > 700 && p.minerals < 150) continue;
    let best = null, bd = Infinity;
    for (const u of workers) {
      if (u.id === ai.scoutId || ai.defenders.has(u.id)) continue;
      if (gasAssignment(u) != null) continue;          // never poach another geyser
      if (u.carrying) continue;                        // let it finish the trip first
      if (!u.order || u.order.type !== 'gather') continue;
      const d = Math.hypot(u.x - r.x, u.y - r.y);
      if (d < bd) { bd = d; best = u; }
    }
    if (best) { world.issue({ type: 'gather', player: ai.player, ids: [best.id], targetId: r.id }); return; }
  }
}

/** True if this worker is committed to a refinery and must not be re-tasked. */
function isGasWorker(world, u) {
  const n = u.gatherNode != null ? world.get(u.gatherNode) : null;
  if (n && n.kind === 'building') return true;
  if (u.order && u.order.type === 'gather' && u.order.targetId != null) {
    const t = world.get(u.order.targetId);
    if (t && (t.defId === 'refinery' || (t.defId === 'geyser' && t.refineryId != null))) return true;
  }
  return false;
}

/**
 * After an expansion finishes, workers do not migrate on their own -- the
 * economy picks the nearest patch to wherever the worker already is. Move one
 * worker per decision tick until the new base is manned.
 */
function balanceMining(world, ai, workers, halls) {
  if (halls.length < 2) return;
  // Not while a base is under attack: "the patch over there is short a worker"
  // is true of the base currently being run over, and this would spend the whole
  // fight walking the evacuees back into it one at a time.
  if (ai.squad.threat) return;
  if (world.tick % (AI_PERIOD * 4) !== 0) return;
  let hungry = null, hungryHall = null;
  for (const h of halls) {
    for (const id of world.resources) {
      const r = world.get(id);
      if (!r || !r.alive || r.defId !== 'mineral' || r.amount <= 0) continue;
      if (Math.hypot(r.x - h.x, r.y - h.y) > 13) continue;
      if ((r.gatherers || 0) < 2) { hungry = r; hungryHall = h; break; }
    }
    if (hungry) break;
  }
  if (!hungry) return;
  // take the surplus worker from the most crowded patch elsewhere
  let donor = null, best = 1;
  for (const w of workers) {
    if (w.id === ai.scoutId || ai.defenders.has(w.id) || w.carrying) continue;
    if (!w.order || w.order.type !== 'gather') continue;
    if (isGasWorker(world, w)) continue;                    // never strip gas
    const n = w.gatherNode != null ? world.get(w.gatherNode) : null;
    if (!n || n.kind === 'building') continue;
    if (Math.hypot(n.x - hungryHall.x, n.y - hungryHall.y) < 13) continue;
    const g = n.gatherers || 0;
    if (g > best) { best = g; donor = w; }
  }
  if (donor) world.issue({ type: 'gather', player: ai.player, ids: [donor.id], targetId: hungry.id });
}

/**
 * Production capacity tracks income, and only ever spends the SURPLUS above the
 * build goal -- so a barracks can never eat the expansion it is supposed to be
 * defending. The surplus test at the bottom is also the anti-float valve: if
 * minerals are piling up on top of a fully funded goal, the bank is the symptom
 * and missing production is the disease.
 */
/**
 * How many barracks the economy can actually feed: a cadence in the early game,
 * capped per town hall, plus one for a scouted rush. One base cannot run five
 * barracks -- trying to is how a scripted AI ends up with a big army, no
 * expansion and no tech -- and the cap is exactly what leaves the surplus that
 * pays for the next base.
 */
/**
 * Minerals an idle gas-using bay is waiting on right now. A factory standing
 * empty because every 150 minerals went into another barracks is the most
 * expensive kind of idle there is -- that barracks makes riflemen the AI
 * already has plenty of, and the tank it displaced is the only unit on the
 * roster that breaks a defended base.
 */
function gasBayHold(world, ai, p) {
  const s = surplus(ai, p);
  let hold = 0;
  for (const b of world.buildingsOf(ai.player)) {
    const def = BuildingDefs[b.defId];
    if (b.constructing || def.isTownHall || !def.produces.length) continue;
    if (!def.produces.some((u) => (UnitDefs[u].cost.gas || 0) > 0)) continue;
    if (b.queue.length) continue;
    const pick = chooseUnit(world, ai, b, p);
    if (!pick) continue;
    const c = UnitDefs[pick].cost;
    if (s.gas < (c.gas || 0)) continue;              // gas-starved, not mineral-starved
    if (s.minerals >= (c.minerals || 0)) continue;   // affordable already
    hold = Math.max(hold, c.minerals || 0);
  }
  return hold;
}

function raxTarget(world, ai, t, halls) {
  const cfg = ai.cfg;
  const byTime = t > 150 ? 2 + Math.floor((t - 150) / cfg.raxCadence) : 2;
  const byHalls = Math.max(2, halls.length * cfg.raxPerHall);
  return Math.min(cfg.maxRax, Math.min(byTime, byHalls) + (ai.intel.rushSuspected ? 1 : 0));
}

function productionBuildings(world, ai, p, t, halls) {
  const cfg = ai.cfg;
  if (ai.emergency >= 1) return;           // capacity is worthless without income
  const s = surplus(ai, p);
  const rax = countBuilt(world, ai.player, 'barracks');
  const raxPending = planned(world, ai, 'barracks');
  if (rax + raxPending >= cfg.maxRax) return;
  if (!world.hasBuilding(ai.player, 'depot')) return;
  if (ai.opener.length) return;                 // finish the opening first
  if (s.minerals < 150 + gasBayHold(world, ai, p)) return;

  // Above the planned target, extra barracks are bought only out of a genuinely
  // embarrassing surplus -- which is what a human does when they look down and
  // see eight hundred minerals.
  const flush = s.minerals > 500 ? 2 : s.minerals > 300 ? 1 : 0;
  const wanted = Math.min(cfg.maxRax, raxTarget(world, ai, t, halls) + flush);
  if (rax + raxPending >= wanted) return;
  if (raxPending >= (s.minerals > 500 ? 2 : 1)) return;
  tryBuild(world, ai, 'barracks');
}

/**
 * Keep every military structure busy; pick the composition here.
 *
 * Two hard rules, and both exist because breaking either one is individually
 * fatal:
 *   - spend only the surplus above the build goal, so the goal actually gets
 *     bought instead of being eternally two riflemen short;
 *   - never queue a unit whose supply is not already free after everything
 *     else in flight, so nothing ever finishes and then stands at the door.
 */
function trainArmy(world, ai, p, room) {
  // No riflemen while there are no workers. Army production off entirely is the
  // difference between an economy that comes back in forty seconds and one that
  // never comes back at all, because every 50 minerals the mines deliver is
  // taken by a barracks before the town hall sees it.
  if (ai.emergency >= 2) return;
  const s = surplus(ai, p);
  const bays = [];
  for (const b of world.buildingsOf(ai.player)) {
    const def = BuildingDefs[b.defId];
    if (b.constructing || def.isTownHall || !def.produces.length) continue;
    bays.push(b);
  }
  // Queue one deep normally; two deep only when the surplus is embarrassing,
  // and even then the supply gate still has to clear for both.
  const depth = s.minerals > 400 ? 2 : 1;
  const gasBay = (b) => BuildingDefs[b.defId].produces.some((u) => (UnitDefs[u].cost.gas || 0) > 0);

  let hold = 0;

  const tryBay = (b, extraHold) => {
    if (b.queue.length >= depth) return 0;
    const pick = chooseUnit(world, ai, b, p);
    if (!pick) return 0;
    const def = UnitDefs[pick];
    const c = def.cost;
    if (room.supply < def.supply) return 0;
    const now = surplus(ai, p);
    if (now.gas < (c.gas || 0)) return 0;
    if (now.minerals - extraHold < (c.minerals || 0)) return -(c.minerals || 0);  // wanted it, could not pay
    if (!train(world, ai, pick, b)) return 0;
    room.supply -= def.supply;
    return 1;
  };

  // Gas units first, and if one is wanted but unaffordable this tick its
  // minerals are held back from the barracks. Without the hold a tank never
  // gets built at all: three barracks take every 50 minerals the instant it
  // lands, the bank never once reaches 150, and the factory stands idle for the
  // whole game behind a queue of riflemen while the gas piles up beside it.
  for (const b of bays) {
    if (!gasBay(b)) continue;
    const r = tryBay(b, 0);
    if (r < 0) hold = Math.max(hold, -r);
  }
  for (const b of bays) {
    if (gasBay(b)) continue;
    tryBay(b, hold);
  }
}

/**
 * What this structure should be making. Options are filtered on TECH ONLY, not
 * on whether we can pay right now -- affordability is trainArmy's job, and it
 * has to know that the factory *wanted* a tank in order to hold the minerals
 * for one. Filtering on cost here instead makes an unaffordable tank vanish
 * from the factory's options entirely, so nothing holds the money, the barracks
 * take every 50 minerals as it lands, and the factory sits idle for four
 * minutes behind five hundred unspent gas.
 */
function chooseUnit(world, ai, b, p) {
  const opts = BuildingDefs[b.defId].produces.filter((u) => techMet(world, ai.player, u));
  if (!opts.length) return null;
  // Tanks are the gas sink, and they do not wait for Siege Tech: an unsieged
  // tank is still 30 explosive damage at range 7, and the research money is
  // already protected by the build goal's reserve, which trainArmy subtracts
  // before it ever sees the gas.
  if (opts.includes('siege_tank')) return 'siege_tank';
  if (opts.includes('wraith')) return p.gas > 180 ? 'wraith' : null;   // gas is for tanks first

  const army = world.unitsOf(ai.player).filter((u) => !UnitDefs[u.defId].isWorker);
  const rif = army.filter((u) => u.defId === 'rifleman').length;
  const bru = army.filter((u) => u.defId === 'bruiser').length;
  // Riflemen are the only infantry that shoots up, and the only one that is not
  // reduced to a quarter damage by armour, so both scouted air and scouted
  // armour skew the mix hard away from bruisers.
  let ratio = ai.cfg.bruiserRatio;
  if (ai.intel.airThreat) ratio = Math.min(ratio, 0.2);
  if (ai.intel.armourThreat) ratio = Math.min(ratio, 0.15);
  const wantBruiser = bru < (rif + bru + 1) * ratio;
  if (wantBruiser && opts.includes('bruiser')) return 'bruiser';
  return opts.includes('rifleman') ? 'rifleman' : opts[0];
}

// ===========================================================================
// Build plumbing
// ===========================================================================
function expirePending(world, ai) {
  const cut = world.tick - 16;
  if (ai.pending.length) ai.pending = ai.pending.filter((x) => x.tick > cut);
}

/** Completed + under construction + walking-to-site + issued-this-instant. */
function planned(world, ai, defId) {
  let n = 0;
  for (const b of world.buildingsOf(ai.player)) if (b.defId === defId && b.constructing) n++;
  for (const u of world.unitsOf(ai.player)) {
    const o = u.order;
    if (o && o.type === 'build' && o.defId === defId && o.buildingId == null) n++;
    if (u.orderQueue) for (const q of u.orderQueue) if (q.type === 'build' && q.defId === defId) n++;
  }
  for (const x of ai.pending) if (x.defId === defId) n++;
  return n;
}
function countUnits(world, player, defId) {
  let n = 0;
  for (const u of world.unitsOf(player)) if (u.defId === defId) n++;
  return n;
}
function countBuilt(world, player, defId) {
  let n = 0;
  for (const b of world.buildingsOf(player)) if (b.defId === defId && !b.constructing) n++;
  return n;
}

function train(world, ai, defId, b) {
  const cost = UnitDefs[defId].cost;
  const p = world.players[ai.player];
  if (!affordNow(ai, p, cost)) return false;
  if (unavailableReason(world, ai.player, defId)) return false;
  world.issue({ type: 'train', player: ai.player, ids: [b.id], defId });
  commit(ai, cost);
  return true;
}

function research(world, ai, defId) {
  const def = ResearchDefs[defId];
  const p = world.players[ai.player];
  if (!def || p.tech.has(defId) || !affordNow(ai, p, def.cost)) return false;
  const b = world.buildingsOf(ai.player).find((x) => !x.constructing && BuildingDefs[x.defId].research.includes(defId));
  if (!b || b.queue.some((q) => q.kind === 'research')) return false;
  world.issue({ type: 'research', player: ai.player, ids: [b.id], defId });
  commit(ai, def.cost);
  return true;
}

/**
 * Pick the worker that costs the least to interrupt. A gas miner costs a lot:
 * pulling one to lay a depot is how a refinery quietly ends up running on two
 * workers for the rest of the game, which is worth about a third of a factory
 * per minute.
 */
function freeWorker(world, ai, near = null) {
  let best = null, bs = Infinity;
  for (const u of world.unitsOf(ai.player)) {
    if (!UnitDefs[u.defId].isWorker) continue;
    if (u.id === ai.scoutId || ai.defenders.has(u.id)) continue;
    const o = u.order;
    if (o && (o.type === 'build' || o.type === 'repair')) continue;
    // prefer an empty-handed miner; a loaded one finishes its trip first
    let s = u.carrying ? 40 : 0;
    if (!o) s -= 5;
    if (isGasWorker(world, u)) s += 60;                     // that is a gas miner
    if (near) s += Math.hypot(u.x - near.x, u.y - near.y) * 0.35;
    if (s < bs) { bs = s; best = u; }
  }
  return best;
}

function tryBuild(world, ai, defId, opts = {}) {
  const p = world.players[ai.player];
  const def = BuildingDefs[defId];
  if (!def) return false;
  if (!affordNow(ai, p, def.cost) || !techMet(world, ai.player, defId)) return false;
  // The rebuild floor. With no workers there is no income, so the last 50
  // minerals in the bank are not spendable -- they are the only worker the AI
  // will ever be able to afford again, and a structure bought with them is a
  // permanent stalemate. Only an actual supply block is allowed through.
  if (ai.emergency >= 1 && !opts.urgent
      && p.minerals - ai.spent.minerals - (def.cost.minerals || 0) < ai.floor) return false;

  if (def.onGeyser) {
    const g = nearestFreeGeyser(world, ai);
    if (!g) return false;
    const w = freeWorker(world, ai, g);
    if (!w) return false;
    world.issue({ type: 'build', player: ai.player, ids: [w.id], defId, x: g.x, y: g.y });
    ai.pending.push({ defId, tick: world.tick });
    commit(ai, def.cost);
    return true;
  }
  const spot = findBuildSpot(world, ai, def, opts);
  if (!spot) return false;
  const w = freeWorker(world, ai, spot);
  if (!w) return false;
  world.issue({ type: 'build', player: ai.player, ids: [w.id], defId, x: spot.x, y: spot.y });
  ai.pending.push({ defId, tick: world.tick });
  commit(ai, def.cost);
  return true;
}

function placeExpansion(world, ai) {
  const site = nextBaseSite(world, ai);
  if (!site) return false;
  if (!world.grid.canPlace(Math.round(site.x - 2), Math.round(site.y - 2), 4, 4)) return false;
  const w = freeWorker(world, ai, site);
  if (!w) return false;
  world.issue({ type: 'build', player: ai.player, ids: [w.id], defId: 'command_center', x: site.x, y: site.y });
  ai.pending.push({ defId: 'command_center', tick: world.tick });
  commit(ai, BuildingDefs.command_center.cost);
  return true;
}

function nearestFreeGeyser(world, ai) {
  const halls = world.buildingsOf(ai.player).filter((b) => BuildingDefs[b.defId].isTownHall);
  let best = null, bd = Infinity;
  for (const id of world.resources) {
    const r = world.get(id);
    if (!r || r.defId !== 'geyser' || r.refineryId) continue;
    for (const h of halls) {
      const d = Math.hypot(r.x - h.x, r.y - h.y);
      if (d < bd && d < 16) { bd = d; best = r; }
    }
  }
  return best;
}

/**
 * Building placement. A spiral that just takes the first legal footprint will
 * happily wall the ramp shut or drop a barracks in the middle of the mineral
 * line, so candidates are scored: stay off the resource lanes, stay off the
 * ramp, and drift towards the enemy so army units spawn on the right side of
 * the base.
 */
function findBuildSpot(world, ai, def, opts = {}) {
  const halls = world.buildingsOf(ai.player).filter((b) => BuildingDefs[b.defId].isTownHall);
  let origin = halls.length ? halls[0] : { x: ai.home.x, y: ai.home.y };
  if (halls.length > 1 && opts.nearMineralLine) {
    // spread turrets: whichever hall has fewest turrets nearby
    let bestH = origin, bestN = Infinity;
    for (const h of halls) {
      let n = 0;
      for (const b of world.buildingsOf(ai.player)) if (b.defId === 'turret' && Math.hypot(b.x - h.x, b.y - h.y) < 12) n++;
      if (n < bestN) { bestN = n; bestH = h; }
    }
    origin = bestH;
  }
  const size = def.size;
  const g = world.grid;
  const fdx = ai.enemyHome.x - origin.x, fdy = ai.enemyHome.y - origin.y;
  const flen = Math.hypot(fdx, fdy) || 1;
  const fx = fdx / flen, fy = fdy / flen;

  const minR = opts.nearMineralLine ? 4 : 5;
  const maxR = opts.nearMineralLine ? 10 : 19;

  let best = null, bs = Infinity;
  for (let r = minR; r <= maxR; r++) {
    const steps = Math.max(14, r * 5);
    for (let i = 0; i < steps; i++) {
      const a = (i / steps) * Math.PI * 2 + r * 0.37;
      const cx = origin.x + Math.cos(a) * r;
      const cy = origin.y + Math.sin(a) * r;
      const tx = Math.round(cx - size / 2), ty = Math.round(cy - size / 2);
      if (!g.canPlace(tx, ty, size, size)) continue;
      let score = r;
      if (nearResource(world, cx, cy, 6.5)) score += 16;
      if (nearRamp(g, tx, ty, size, 2)) score += 24;
      score -= ((cx - origin.x) * fx + (cy - origin.y) * fy) * 0.55;
      if (score < bs) { bs = score; best = { x: tx + size / 2, y: ty + size / 2 }; }
    }
    if (best && r >= minR + 6) break;         // good enough; do not scan the whole base
  }
  return best;
}

function nearResource(world, x, y, r) {
  for (const id of world.resources) {
    const n = world.get(id);
    if (!n || !n.alive) continue;
    if (Math.abs(n.x - x) > r + 2 || Math.abs(n.y - y) > r + 2) continue;
    if (Math.hypot(n.x - x, n.y - y) < r) return true;
  }
  return false;
}
function nearRamp(g, tx, ty, size, pad) {
  for (let y = ty - pad; y < ty + size + pad; y++)
    for (let x = tx - pad; x < tx + size + pad; x++)
      if (g.inBounds(x, y) && g.ramp[y * g.w + x]) return true;
  return false;
}

function nextBaseSite(world, ai) {
  let best = null, bd = Infinity;
  for (const b of world.bases) {
    let taken = false;
    for (const pl of [0, 1]) {
      for (const x of world.buildingsOf(pl)) {
        if (BuildingDefs[x.defId].isTownHall && Math.hypot(x.x - b.cc.x, x.y - b.cc.y) < 7) { taken = true; break; }
      }
      if (taken) break;
    }
    if (taken) continue;
    // Prefer our own half; the enemy's natural is a late-game land grab.
    const d = Math.hypot(b.cc.x - ai.home.x, b.cc.y - ai.home.y) + (b.owner === ai.player ? 0 : 40);
    if (d < bd) { bd = d; best = b; }
  }
  return best ? { ...best.cc } : null;
}

// ===========================================================================
// Scouting
// ===========================================================================
function scout(world, ai) {
  const cfg = ai.cfg;
  if (!ai.scoutSent) {
    if (world.time < cfg.scoutAt) return;
    if (ai.emergency >= 1) return;          // the last workers are not tourists
    const w = freeWorker(world, ai);
    if (!w) return;
    ai.scoutId = w.id; ai.scoutSent = true; ai.scoutLeg = 0;
    world.issue({ type: 'move', player: ai.player, ids: [w.id], x: ai.enemyHome.x, y: ai.enemyHome.y });
    return;
  }
  const s = world.alive(ai.scoutId);
  if (!s) {
    if (ai.scoutId != null) { ai.scoutId = null; ai.scoutDeadTick = world.tick; }
    // A dead scout in the enemy base is itself information; do not feed another
    // one in immediately, but keep an eye on the map later in the game.
    if (world.time > 300 && world.tick - ai.scoutDeadTick > 24 * 90) { ai.scoutSent = false; }
    return;
  }
  if (s.order) return;                        // still travelling

  // Loop: enemy main -> enemy natural -> centre -> enemy main ...
  const legs = scoutLegs(world, ai);
  ai.scoutLeg = (ai.scoutLeg + 1) % legs.length;
  const to = legs[ai.scoutLeg];
  world.issue({ type: 'move', player: ai.player, ids: [s.id], x: to.x, y: to.y });
}

function scoutLegs(world, ai) {
  const foe = 1 - ai.player;
  const enemyNat = world.bases.find((b) => b.owner === foe && !b.isMain);
  return [
    { x: ai.enemyHome.x, y: ai.enemyHome.y },
    enemyNat ? { x: enemyNat.cc.x, y: enemyNat.cc.y } : { x: ai.enemyHome.x, y: ai.enemyHome.y },
    { x: world.grid.w / 2, y: world.grid.h / 2 },
  ];
}

// ===========================================================================
// Army
// ===========================================================================
function armyUnits(world, ai) {
  const out = [];
  for (const u of world.unitsOf(ai.player)) {
    if (UnitDefs[u.defId].isWorker) continue;
    if (ai.harass.ids.has(u.id)) continue;
    out.push(u);
  }
  return out;
}
function valueOf(u) {
  const d = UnitDefs[u.defId];
  return d.supply * (0.35 + 0.65 * (u.hp / u.maxHp));
}
function sumValue(list) { let s = 0; for (const u of list) s += valueOf(u); return s; }

function army(world, ai, p) {
  const sq = ai.squad;
  const all = armyUnits(world, ai);
  const byId = new Map(all.map((u) => [u.id, u]));
  for (const id of sq.ids) if (!byId.has(id)) sq.ids.delete(id);
  for (const id of ai.garrison) if (!byId.has(id)) ai.garrison.delete(id);

  wraithHarass(world, ai, p);
  ai.holdPoint = null;          // ground the tanks have been told to take, if any

  const threat = homeThreat(world, ai);
  sq.threat = threat;
  updateHomeStance(world, ai, all, threat);

  // ---- defend pre-empts everything -------------------------------------
  if (threat) {
    const committed = sq.state === 'attack' && sq.ids.size > 0;
    const serious = threat.value >= 7 || threat.hittingBuildings || !committed;
    if (serious && sq.state !== 'defend') { setState(world, sq, 'defend'); clearPoint(ai, 'defend'); }
  } else if (sq.state === 'defend' && world.tick - sq.stateSince > 24 * 3) {
    setState(world, sq, 'mass');
  }

  workerDefence(world, ai, p, threat);
  evacuateWorkers(world, ai, all, threat);

  // Mopping up. There is nothing left to garrison against and nothing left to
  // mass for, so the whole army goes looking -- see doHunt().
  const hunting = sq.state !== 'defend' && shouldHunt(world, ai, all);
  updateGarrison(world, ai, all, hunting ? null : threat, hunting);

  const field = all.filter((u) => !ai.garrison.has(u.id));

  if (hunting) { doHunt(world, ai, field); return; }
  // Contact. Fold the search parties back into one army and go and kill it,
  // from where the army actually is -- not from the staging point it left
  // several minutes and half a map ago.
  if (sq.state === 'hunt') {
    endHunt(world, ai);
    if (field.length) launchAttack(world, ai, field, squadAnchor(world, ai, field));
    else setState(world, sq, 'mass');
  }

  switch (sq.state) {
    case 'defend':  doDefend(world, ai, all, threat); break;
    case 'attack':  doAttack(world, ai, p, field); break;
    case 'retreat': doRetreat(world, ai, field); break;
    default:        doMass(world, ai, p, field); break;
  }

  if (sq.state !== 'defend' && ai.garrison.size) holdGarrison(world, ai, all);
}

function setState(world, sq, s) { sq.state = s; sq.stateSince = world.tick; }

/**
 * A handful of units stay home once there is a second base worth losing. It is
 * a small number on purpose: enough that a stray raid does not walk into an
 * undefended mineral line, never enough to matter to the push. When the whole
 * army is needed at home the defend state overrides this anyway.
 */
function updateGarrison(world, ai, all, threat, hunting = false) {
  const cfg = ai.cfg;
  const halls = world.buildingsOf(ai.player).filter((b) => BuildingDefs[b.defId].isTownHall && !b.constructing);
  if (hunting) { ai.garrison.clear(); return; }
  if (halls.length < 2 || threat || ai.armyValue < cfg.garrisonAt) { ai.garrison.clear(); return; }
  const want = Math.min(cfg.garrisonMax, Math.floor(all.length * 0.2));
  for (const id of [...ai.garrison]) if (ai.garrison.size > want) ai.garrison.delete(id);
  if (ai.garrison.size >= want) return;
  // Prefer riflemen: they shoot air, which is what a home guard is actually for.
  const cands = all.filter((u) => !ai.garrison.has(u.id) && !ai.squad.ids.has(u.id))
    .sort((a, b) => (a.defId === 'rifleman' ? -1 : 0) - (b.defId === 'rifleman' ? -1 : 0));
  for (const u of cands) { if (ai.garrison.size >= want) break; ai.garrison.add(u.id); }
}

function holdGarrison(world, ai, all) {
  const guard = all.filter((u) => ai.garrison.has(u.id));
  if (!guard.length) return;
  const st = stagingPoint(world, ai);
  const pt = stablePoint(ai, 'garrison', st.x, st.y, 3);
  issueGroup(world, ai, 'attackmove', guard.filter((u) => !u.sieged && u.siegeTimer <= 0), pt.x, pt.y);
}

// ------------------------------------------------------------ home ground --
/**
 * The top of our own ramp -- the one piece of ground on this map that is worth
 * more than the army standing on it.
 *
 * The main sits on a plateau behind a single ramp, and fog.js implements the
 * StarCraft height rule: a ground unit on low ground CANNOT SEE onto high
 * ground, and combat.js will not let anything shoot what it cannot see. An army
 * on the crest is therefore not merely "in a choke". It is shooting at units
 * that cannot shoot back until they have finished walking up, and a sieged tank
 * beside it covers the whole ramp and six tiles of the ground below it from
 * outside the range of everything the attacker owns.
 *
 * Walking DOWN to meet that same attack -- which is what a threat centroid
 * asks for, because the centroid of an army coming up a ramp is on the ramp --
 * throws all of that away and fights the same fight on flat ground.
 *
 * Terrain never changes, so this is computed once.
 */
function rampPoint(world, ai) {
  if (ai.rampPoint !== null) return ai.rampPoint || null;
  ai.rampPoint = false;                    // computed, and there isn't one
  const g = world.grid;
  const hx = ai.home.x | 0, hy = ai.home.y | 0;
  const lvl = g.heightAt(hx, hy);
  if (lvl === 0) return null;              // our main is not high ground

  // The ramp cluster nearest the town hall is OUR ramp. Every other ramp on the
  // map belongs to somebody else's problem.
  let bd = Infinity;
  for (let y = 0; y < g.h; y++) {
    for (let x = 0; x < g.w; x++) {
      if (!g.ramp[y * g.w + x]) continue;
      const d = Math.hypot(x + 0.5 - ai.home.x, y + 0.5 - ai.home.y);
      if (d < bd) bd = d;
    }
  }
  if (!Number.isFinite(bd)) return null;
  let sx = 0, sy = 0, n = 0;
  for (let y = 0; y < g.h; y++) {
    for (let x = 0; x < g.w; x++) {
      if (!g.ramp[y * g.w + x]) continue;
      if (Math.hypot(x + 0.5 - ai.home.x, y + 0.5 - ai.home.y) > bd + 6) continue;
      sx += x + 0.5; sy += y + 0.5; n++;
    }
  }
  const mouth = { x: sx / n, y: sy / n };
  const dx = ai.home.x - mouth.x, dy = ai.home.y - mouth.y;
  const l = Math.hypot(dx, dy) || 1;
  for (let s = 1; s <= 24; s++) {
    const px = mouth.x + (dx / l) * s, py = mouth.y + (dy / l) * s;
    const tx = px | 0, ty = py | 0;
    if (!g.inBounds(tx, ty)) break;
    const i = ty * g.w + tx;
    if (g.ramp[i] || g.height[i] !== lvl || g.cliff[i]) continue;
    // The FIRST tile of our own plateau: on the crest, not behind it.
    //
    // Standing a few tiles further in reads safer and measurably is not. It
    // hands over the choke -- the attacker finishes walking up unopposed,
    // deploys on the flat and fights the same open battle three tiles deeper in
    // our base. Held at the crest the identical attack arrives in single file
    // and dies in it. Measured over seeds 1-10: crest 9W-1L-0D, three tiles
    // back 6W-2L-2D.
    const e = g.nearestPassable(mouth.x + (dx / l) * s, mouth.y + (dy / l) * s);
    ai.rampPoint = { x: e.x + 0.5, y: e.y + 0.5 };
    return ai.rampPoint;
  }
  return null;
}

/**
 * Fight it in the open, or fall back and fight it on our ground?
 *
 * Latched, with hysteresis, because this is a posture and not a per-tick
 * opinion: an army that re-decides it four times a second walks halfway to the
 * ramp, changes its mind, walks halfway back, and dies in the middle doing
 * neither. It turns on for a fight we are LOSING and only turns off once we are
 * clearly winning again or the attacker has gone home.
 */
function updateHomeStance(world, ai, all, threat) {
  if (!rampPoint(world, ai)) { ai.holdRamp = false; return; }
  if (threat) {
    // What is actually coming, not just what is standing on a building right
    // now: the tail of a 30-supply attack is still walking in.
    ai.lastThreatValue = Math.max(threat.value, visibleEnemyArmyNear(world, ai, threat, 18));
    ai.lastThreatTick = world.tick;
  }
  // What we are up against is NOT what is currently on screen, and on this map
  // it never can be: the height rule blanks our vision at the crest, so an
  // attack massing at the bottom of our own ramp reads as the four units that
  // have stepped far enough forward to be seen. Deciding "I outnumber that" off
  // the visible four is how twelve units walked down a ramp into twenty-five on
  // seeds 13 and 21. The believed enemy army -- earned intel, decayed, floored
  // by the production we scouted -- is the honest number to weigh ourselves
  // against, and it is the same one armyFloor() already trusts.
  const believed = Math.max(ai.lastThreatValue, ai.intel.enemyArmyEst * 0.9);
  ai.believed = believed;
  const mine = sumValue(all);
  if (!ai.holdRamp) {
    if (threat && believed >= 8 && mine < believed * 1.1) ai.holdRamp = true;
  } else if (mine > believed * 1.5 || world.tick - ai.lastThreatTick > TICK_RATE * 45) {
    ai.holdRamp = false;
  }
}

/**
 * Where the defence actually stands. Two rules, in this order:
 *
 *   1. NEVER WALK DOWN YOUR OWN RAMP. An army massing at the bottom of it reads
 *      to homeThreat() as four supply, because four is all we can see -- the
 *      height rule cuts our vision off at the crest exactly as it cuts theirs.
 *      Marching down to meet "four supply" is how twelve units and a tank
 *      became five units in ten seconds on seed 13. The ground is right there
 *      and it is free; take it and make them come up.
 *   2. Once the fight in the open is lost (holdRamp), everything that is not
 *      already engaged inside the main comes back to the same point. That is
 *      the decision to write off the low-ground base and hold the high one.
 *
 * `dug` says the stand is a chosen POSITION rather than a place a fight
 * happens to be, which is what lets the tanks commit to it before contact.
 */
function defenceStand(world, ai, threat) {
  const hold = rampPoint(world, ai);
  if (!threat) {
    if (hold && ai.holdRamp) return { x: hold.x, y: hold.y, dug: true };
    const st = stagingPoint(world, ai);
    return { x: st.x, y: st.y, dug: false };
  }
  if (hold) {
    const g = world.grid;
    const lvl = g.heightAt(ai.home.x | 0, ai.home.y | 0);
    const below = g.heightAt(threat.x | 0, threat.y | 0) < lvl;
    // ...unless we clearly outgun them, in which case going down and killing
    // it is worth more than the terrain is. Turtling on the ramp with an army
    // half again the size of the thing outside it is not caution, it is a
    // twenty-minute draw: three of them, measured.
    const strong = sumValue(armyUnits(world, ai)) > ai.believed * 1.3;
    if (below && !strong && Math.hypot(threat.x - hold.x, threat.y - hold.y) < 16) {
      return { x: hold.x, y: hold.y, dug: true };
    }
    const upHere = !below && Math.hypot(threat.x - ai.home.x, threat.y - ai.home.y) < 26;
    if (ai.holdRamp && !upHere) return { x: hold.x, y: hold.y, dug: true };
  }
  return { x: threat.x, y: threat.y, dug: false };
}

/** Enemy army standing on top of anything we own. Vision-gated, like a player. */
function homeThreat(world, ai) {
  const foe = 1 - ai.player;
  const mine = [...world.buildingsOf(ai.player)];
  if (!mine.length) return null;
  let value = 0, sx = 0, sy = 0, n = 0, hitting = false;
  for (const u of world.unitsOf(foe)) {
    const d = UnitDefs[u.defId];
    if (d.isWorker) continue;
    if (!canSee(world, ai.player, u)) continue;
    let near = false;
    for (const b of mine) {
      if (Math.hypot(b.x - u.x, b.y - u.y) <= 15) { near = true; break; }
    }
    if (!near) continue;
    value += d.supply; sx += u.x; sy += u.y; n++;
  }
  for (const b of mine) if (b.lastDamagedTick != null && world.tick - b.lastDamagedTick < 48) hitting = true;
  if (!n) return null;
  return { value, x: sx / n, y: sy / n, count: n, hittingBuildings: hitting };
}

/**
 * A tank that is already inside its own siege range of the fight should set up,
 * not walk. Ordering it to attack-move onto the enemy trades a 70-damage splash
 * shell at range 12 for a 30-damage shot at range 7 -- and does it while the
 * tank is out of position, which is the worst of both.
 */
function shouldSetUp(world, ai, u, at) {
  if (!UnitDefs[u.defId].canSiege) return false;
  if (!world.players[ai.player].tech.has('siege_tech')) return false;
  const d = Math.hypot(u.x - at.x, u.y - at.y);
  return d >= 5.0 && d <= 12.0;
}

/**
 * The ground the TANKS take, which is not always the ground the infantry takes.
 *
 * A tank walks at 4.4 and needs two seconds to deploy; it is the one unit on the
 * roster that cannot afford to decide where it is fighting after the fight has
 * started. So when the attack is coming up our own ramp, the tanks stop at the
 * crest and shell down it whatever the infantry does -- the riflemen can go
 * meet them at the bottom if we are strong enough to want that fight, and the
 * artillery still fires from twelve tiles up a cliff the attacker cannot see
 * onto. That split is most of the difference between a tank that spends the
 * engagement dealing 70 and one that spends it dealing 30.
 */
function tankGround(world, ai, threat, stand) {
  if (stand.dug) return stand;
  const hold = rampPoint(world, ai);
  if (!hold || !threat) return null;
  const g = world.grid;
  const lvl = g.heightAt(ai.home.x | 0, ai.home.y | 0);
  if (g.heightAt(threat.x | 0, threat.y | 0) >= lvl) return null;   // no cliff between us
  return Math.hypot(threat.x - hold.x, threat.y - hold.y) < 16 ? hold : null;
}

function doDefend(world, ai, all, threat) {
  const sq = ai.squad;
  const to = defenceStand(world, ai, threat);
  sq.ids = new Set(all.map((u) => u.id));
  // A threat centroid drifts every tick as its units move; snapping it to a
  // tile and holding it there is what keeps the defenders in one flow-field
  // group instead of N groups of one. (The ramp point is already a fixed tile,
  // so falling back costs no re-grouping at all -- which is half the reason it
  // is a better place to fight.)
  const pt = stablePoint(ai, 'defend', to.x, to.y, 4);
  const tg = tankGround(world, ai, threat, to);
  ai.holdPoint = tg ? stablePoint(ai, 'tankhold', tg.x, tg.y, 2) : null;
  unsiegeFarFrom(world, ai, all, ai.holdPoint || pt, 13);
  const setUp = all.filter((u) => shouldSetUp(world, ai, u, pt));
  sq.sieging = setUp.length > 0;
  const holding = new Set(setUp.map((u) => u.id));

  const movers = [], guns = [];
  for (const u of all) {
    if (u.sieged || u.siegeTimer > 0) continue;
    if (ai.holdPoint && UnitDefs[u.defId].canSiege) { guns.push(u); continue; }
    if (holding.has(u.id)) continue;
    movers.push(u);
  }
  issueGroup(world, ai, 'attackmove', movers, pt.x, pt.y);
  if (guns.length) issueGroup(world, ai, 'attackmove', guns, ai.holdPoint.x, ai.holdPoint.y);
  setRally(world, ai, pt.x, pt.y);
}

/** Mass at the staging point and wait for the wave threshold. */
function doMass(world, ai, p, all) {
  const sq = ai.squad;
  const cfg = ai.cfg;
  const st = stagingPoint(world, ai);
  const pt = stablePoint(ai, 'mass', st.x, st.y, 3);
  // Massing on the high ground IS taking a position, so the tanks dig in there
  // rather than waiting to be told. Ground chosen before the attack arrives is
  // the entire difference between a siege line and a tank that gets caught
  // unpacking.
  ai.holdPoint = ai.holdRamp ? pt : null;

  sq.ids = new Set(all.map((u) => u.id));
  unsiegeFarFrom(world, ai, all, pt, 12);
  const movers = all.filter((u) => !u.sieged && u.siegeTimer <= 0
    && Math.hypot(u.x - pt.x, u.y - pt.y) > 6);
  issueGroup(world, ai, 'attackmove', movers, pt.x, pt.y);
  setRally(world, ai, pt.x, pt.y);

  const value = sumValue(all);
  const wave = Math.min(sq.wave, cfg.waveValues.length - 1);
  // The ladder says how big waves GROW. It is relaxed in exactly one case: we
  // have never scouted a single piece of enemy military production and we can
  // see nothing worth calling an army. Massing thirty supply to attack an
  // opponent who has none is not patience, it is a scripted AI waiting for a
  // number, and it is how a won game becomes a fifteen-minute stalemate.
  //
  // The relaxation is deliberately this narrow. Scaling the threshold off the
  // army estimate in general reads reasonable and is a disaster: the estimate
  // is low precisely when scouting has been poor, so the AI throws fourteen
  // supply into a defended base at 6:40 on the strength of not having looked.
  const intel = ai.intel;
  const blind = intel.enemyRax === 0 && !intel.enemyFactory && !intel.enemyStarport;
  const need = (blind && intel.enemyArmyEst < cfg.waveValues[0])
    ? cfg.waveValues[0] : cfg.waveValues[wave];
  const t = world.time;

  // Cohesion gate: a "push" that leaves with half its units still walking out
  // of the main is a trickle wearing a push's clothes.
  const gathered = all.filter((u) => Math.hypot(u.x - pt.x, u.y - pt.y) < 11).length;
  const massed = all.length > 0 && gathered >= Math.ceil(all.length * 0.65);

  let go = false;
  if (sq.wave === 0) {
    // First push is a timing attack: it leaves on the clock, not on a number.
    if (t >= cfg.firstAttackTime && value >= cfg.firstAttackValue && massed) go = true;
    if (t >= cfg.firstAttackDeadline && value >= Math.max(3, cfg.firstAttackValue - 3)) go = true;
    if (ai.intel.rushSuspected && t < cfg.firstAttackDeadline) go = false;
  } else {
    if (value >= need && massed) go = true;
    if (value >= need * 1.3) go = true;
    // Do not sit on a huge idle army because a threshold has not been met.
    //
    // Both of the obvious ways to relax this ladder further were tried and
    // measured on seeds 1-8, and both are traps:
    //   * "attack once you outweigh what you believe they have" -- 8W-16L. The
    //     belief is lowest exactly when scouting has been worst, so it
    //     green-lights walking fifteen supply into thirty.
    //   * "drop the bar to 45% of the ladder after two and a half minutes of
    //     standing still" -- 2W-6L, same failure with a clock in front of it.
    // The ladder stays. Games that run the twenty-minute cap with both armies
    // alive are two macro players who cannot break each other, which is a
    // different problem from a won game that will not end.
    if (value >= need * 0.7 && world.tick - sq.lastWaveTick > 24 * 75) go = true;
    // ...but never walk a smaller army into a bigger one just because a timer
    // said so. Feeding waves into a defended base one at a time is the same
    // trickle as feeding units, only slower and more expensive.
    if (value < ai.intel.enemyArmyEst * 1.15 && value < cfg.waveValues[cfg.waveValues.length - 1]) go = false;
  }
  // We have razed everything we ever found and there is nothing on screen.
  // There is no wave threshold to wait for here -- the only thing left to do is
  // go and look, and sitting at the staging point instead is how a won game
  // becomes a twenty-five minute draw. Gated on having ACTUALLY scouted enemy
  // structures at some point, so the opening (memory legitimately empty because
  // nobody has looked yet) is untouched.
  if (all.length && ai.sawEnemyStructures && !knowOfEnemy(world, ai)) go = true;
  if (go && all.length) launchAttack(world, ai, all);
}

function stagingPoint(world, ai) {
  // Losing the fight in the open. Everything -- massing, regrouping, the home
  // guard, the wraiths coming back -- collects on the high ground behind the
  // ramp instead of in front of the natural. Without this the state machine
  // pulls back to the ramp to defend and then walks straight back out to the
  // staging point the moment the attacker steps out of vision.
  if (ai.holdRamp) { const h = rampPoint(world, ai); if (h) return h; }
  // Once the natural exists, the army holds in front of it instead of in the
  // main -- that is what actually defends the expansion.
  const halls = world.buildingsOf(ai.player).filter((b) => BuildingDefs[b.defId].isTownHall && !b.constructing);
  if (halls.length < 2) return ai.staging;
  let fwd = null, bd = -1;
  for (const h of halls) {
    const d = Math.hypot(h.x - ai.enemyHome.x, h.y - ai.enemyHome.y);
    if (bd < 0 || d < bd) { bd = d; fwd = h; }
  }
  const dx = ai.enemyHome.x - fwd.x, dy = ai.enemyHome.y - fwd.y;
  const len = Math.hypot(dx, dy) || 1;
  const pt = world.grid.nearestPassable(fwd.x + (dx / len) * 9, fwd.y + (dy / len) * 9);
  return { x: pt.x + 0.5, y: pt.y + 0.5 };
}

function launchAttack(world, ai, all, from = null) {
  const sq = ai.squad;
  const st = from || stagingPoint(world, ai);
  sq.target = pickTarget(world, ai, st);
  sq.route = buildRoute(world, ai, st, sq.target);
  sq.wpIdx = 0;
  sq.wpSince = world.tick;
  sq.standoffSince = -1; sq.pushIn = false;
  sq.startValue = sumValue(all);
  sq.peakValue = sq.startValue;
  sq.ids = new Set(all.map((u) => u.id));
  sq.lastWaveTick = world.tick;
  if (sq.launchTick < 0) sq.launchTick = world.tick;
  setState(world, sq, 'attack');
  clearPoint(ai, 'attack');
  clearPoint(ai, 'rendez');
  // Everything comes. A tank left dug in on the ramp because the wave launched
  // while it was holding ground is a third of the push that never leaves.
  ai.holdRamp = false;
  ai.holdPoint = null;
  unsiegeFarFrom(world, ai, all, st, 0);
  const wp = stablePoint(ai, 'attack', sq.route[0].x, sq.route[0].y, 0);
  issueGroup(world, ai, 'attackmove', all.filter((u) => !u.sieged), wp.x, wp.y, true);
}

function doAttack(world, ai, p, all) {
  const sq = ai.squad;
  if (!all.length) { sq.wave++; setState(world, sq, 'mass'); return; }

  // fold in / chase down reinforcements
  const anchor = squadAnchor(world, ai, all);
  const squad = [], stragglers = [];
  for (const u of all) {
    if (sq.ids.has(u.id)) { squad.push(u); continue; }
    if (Math.hypot(u.x - anchor.x, u.y - anchor.y) <= 13) { sq.ids.add(u.id); squad.push(u); }
    else stragglers.push(u);
  }

  const value = sumValue(squad);
  if (value > sq.peakValue) sq.peakValue = value;

  // ---- is this fight lost? ---------------------------------------------
  // Measured against the PEAK this wave reached, not against the value it left
  // home with. Reinforcements fold in continuously, so a start-value test on a
  // wave that left with eight supply is a test that can never fire again for
  // the rest of the game -- which is exactly how the previous version ended up
  // grinding a single "attack" from four minutes to fifteen without once
  // regrouping. Not for the first few seconds either: a push that turns around
  // the instant it takes a hit reads as indecision, and a timing attack is
  // supposed to trade.
  const opening = sq.wave === 0;
  const ratio = opening ? ai.cfg.firstWaveRetreat : ai.cfg.retreatRatio;
  const odds = opening ? ai.cfg.firstWaveOdds : 1.35;
  const commit = opening ? ai.cfg.firstWaveCommit : ai.cfg.commitSeconds;

  const settled = world.tick - sq.stateSince > 24 * commit;
  if (settled) {
    const wiped = value < sq.peakValue * ratio;
    // Also read the fight in front of us: clearly outnumbered and already down
    // is a lost fight even if the ratio has not tripped yet.
    const defenders = visibleEnemyArmyNear(world, ai, anchor, 16);
    const outgunned = defenders > value * odds && value < sq.peakValue * 0.85;
    if (wiped || outgunned) {
      sq.wave++;
      setState(world, sq, 'retreat');
      clearPoint(ai, 'retreat');
      return;
    }
  }

  // refresh the objective as things die / get scouted
  if (world.tick % (AI_PERIOD * 8) === 0) {
    const t = pickTarget(world, ai, anchor);
    // Normally the objective only moves when it moves a long way -- re-routing
    // an army every two seconds is how a push turns into a wander. While
    // SWEEPING for a survivor, though, the whole point is to visit points that
    // are close together, and any deadband at all means the search stalls on
    // the first cell it picked and the army stands there idle forever.
    const slack = ai.targetKind === 'sweep' ? 0.5 : 12;
    if (!sq.target || Math.hypot(t.x - sq.target.x, t.y - sq.target.y) > slack) {
      sq.target = t;
      sq.route = buildRoute(world, ai, anchor, t);
      sq.wpIdx = 0; sq.wpSince = world.tick;
      sq.standoffSince = -1; sq.pushIn = false;
    }
  }
  if (!sq.route || !sq.route.length) sq.route = buildRoute(world, ai, anchor, sq.target || ai.enemyHome);

  // waypoint hop with a cohesion gate: this is what makes it a push rather
  // than a stream of individuals walking into the enemy one at a time
  const last = sq.route.length - 1;
  const wp = sq.route[Math.min(sq.wpIdx, last)];
  if (sq.wpIdx < last) {
    const movers = squad.filter((u) => !u.sieged);
    const near = movers.filter((u) => Math.hypot(u.x - wp.x, u.y - wp.y) < 9).length;
    const stalled = world.tick - sq.wpSince > 24 * 12;
    if ((movers.length && near >= Math.ceil(movers.length * 0.7)) || stalled) {
      sq.wpIdx++; sq.wpSince = world.tick;
      clearPoint(ai, 'attack');
    }
  }
  // ---- the siege push ---------------------------------------------------
  // On the final leg, an army with tanks does not walk into the objective; it
  // stops short and sets up. Nine and a half tiles is chosen against what is
  // shooting back: outside a rifleman's 4.5 and a tank's 7, inside a sieged
  // tank's 12. Walking the last ten tiles instead means the tanks fight at
  // range 7 in mobile mode for 30 damage a shell, which is the same as not
  // having built them.
  let raw = sq.route[Math.min(sq.wpIdx, last)];
  sq.sieging = false;
  if (sq.wpIdx >= last && sq.target && !sq.pushIn && ai.targetKind !== 'sweep'
      && ai.cfg.siegePush && p.tech.has('siege_tech')
      && squad.some((u) => UnitDefs[u.defId].canSiege)) {
    const d = Math.hypot(anchor.x - sq.target.x, anchor.y - sq.target.y);
    if (d < 17) { raw = siegeStandoff(world, sq.target, anchor, 9.5); sq.sieging = true; }
  }
  // A stand-off is for shelling something. Half a minute of one with nothing in
  // sight means the building we are besieging is a ghost in fog memory -- and a
  // ghost is only erased by SEEING its empty footprint, which is nine and a
  // half tiles further than a marine can see. That is a permanent stalemate:
  // the army parks, the memory never clears, the objective never changes. So
  // once the stand-off has gone quiet, latch `pushIn` and walk onto the thing.
  if (sq.sieging) {
    if (sq.standoffSince < 0 || anyEnemyVisibleNear(world, ai, anchor, 14)) sq.standoffSince = world.tick;
    else if (world.tick - sq.standoffSince > TICK_RATE * 12) sq.pushIn = true;
  }
  const goal = stablePoint(ai, 'attack', raw.x, raw.y, 2);
  const holdTanks = sq.sieging && sq.target
    ? new Set(squad.filter((u) => shouldSetUp(world, ai, u, sq.target)).map((u) => u.id))
    : null;
  issueGroup(world, ai, 'attackmove',
    squad.filter((u) => !u.sieged && u.siegeTimer <= 0 && !(holdTanks && holdTanks.has(u.id))),
    goal.x, goal.y);

  // Reinforcements rendezvous on a STABLE tile near the squad, never on the
  // anchor itself: the anchor moves every tick, so an order re-issued to it
  // gives every straggler its own flow-field group and its own lonely death.
  if (stragglers.length) {
    const rz = stablePoint(ai, 'rendez', anchor.x, anchor.y, 7);
    issueGroup(world, ai, 'attackmove', stragglers.filter((u) => !u.sieged), rz.x, rz.y);
  }
  const rally = stablePoint(ai, 'rally', anchor.x, anchor.y, 8);
  setRally(world, ai, rally.x, rally.y);

  if (sq.arrivedTick < 0 && sq.target) {
    for (const u of squad) {
      if (Math.hypot(u.x - ai.enemyHome.x, u.y - ai.enemyHome.y) < 18) { sq.arrivedTick = world.tick; break; }
    }
  }
}

// ============================================================================
// The hunt -- finishing a won game.
//
// Once the last structure we knew about is rubble, the only thing standing
// between us and the victory banner is a straggler somewhere in the fog: four
// workers that ran, a refinery in a corner, a command centre being rebuilt
// behind us. checkVictory() only fires when a player owns NOTHING, so this is
// not a flourish -- it is the difference between a win and a twenty-minute
// draw, and two blind playtesters hit exactly that draw.
//
// The old behaviour walked the WHOLE army over the map one five-tile cell at a
// time. It always found the survivor eventually and it took minutes, because a
// hundred and twenty supply searching in single file covers exactly as much
// ground per second as one marine does.
//
// So: split up. A human hunting the last building drops the army into four or
// five control groups and sends each one somewhere different -- the base sites
// and mineral lines first, because that is where a worker actually runs to,
// then the corners, then everything else. That is all this is:
//
//   * parties are formed once, on distinct SEED CELLS chosen to be far apart,
//     and each unit joins the party whose seed it is nearest -- balanced, so
//     six parties of ten rather than one of fifty-five and five of one;
//   * each party then floods its own patch, taking the nearest unsearched cell
//     to itself and skipping cells that clearly belong to another party, so
//     five brooms sweep five regions instead of five units following one;
//   * every cell anybody can SEE is struck off for everybody. Vision is
//     shared, so parties never re-search each other's ground;
//   * a party that sees an enemy goes and kills it on its own -- the other
//     parties keep sweeping, because converging fifty units on one fleeing
//     worker is how the search stalls;
//   * and a couple of workers are sent to the far corners, which is what the
//     spare SCVs are for once the game is decided.
//
// The moment a STRUCTURE lands in fog memory the hunt ends and the parties
// re-form into one army (see army()): a rebuilt base is worth converging on,
// a running worker is not.
// ============================================================================
const HUNT_PARTY_MIN = 5;             // units per party -- a party has to be able to kill a town hall
const HUNT_GIVEUP = TICK_RATE * 22;   // cell we cannot seem to reach -- call it searched

/**
 * Are we mopping up? Two cases, and both want the army in several places at
 * once:
 *
 *   * nothing of the enemy's is left in fog memory, so the only move is to go
 *     and look;
 *   * or there is plenty left in memory but nothing that shoots back. Fifteen
 *     buildings razed one at a time by one blob is ninety seconds of walking;
 *     the same fifteen razed six at a time is twenty. Measured on seed 3, that
 *     walk was the whole of the gap between "the player has no army left" and
 *     the victory banner -- the search that everybody blames took five seconds.
 *
 * The second case is gated on VISION, not on the entity list: no enemy combat
 * unit anywhere in sight for a good while. A player splits into control groups
 * when the fight is over and not before, and that is the only honest signal
 * available for "the fight is over".
 */
function shouldHunt(world, ai, field) {
  if (!field.length) return false;
  // Never during the opening: an empty memory before we have ever scouted one
  // is "we have not looked", not "we have razed everything".
  if (!ai.sawEnemyStructures) return false;
  if (!rememberedEnemyStructure(world, ai)) return true;

  // Splitting up in front of an army that is merely out of sight is how you
  // lose a won game, so the demolition split asks for all four of: enough
  // units to make real groups, an army that is already ON the objective, a
  // long quiet, and -- once split -- one sighting to re-form.
  const sq = ai.squad;
  const quiet = world.tick - ai.intel.lastContactTick;
  if (field.length < 8) return false;
  if (sq.state === 'hunt') return quiet > TICK_RATE * 8;
  if (sq.state !== 'attack') return false;
  if (quiet <= TICK_RATE * 14) return false;
  // ON the objective, not merely walking towards one: a push that has not made
  // contact yet has not seen an enemy army for perfectly innocent reasons, and
  // splitting it up on the way in is how it arrives in pieces.
  //
  // Measured on UNITS, not on the squad's centroid. The centroid of an army
  // besieging a base from stand-off range, with a tail still arriving, sits
  // twenty-odd tiles from the nearest building for the whole demolition -- so a
  // centroid test never fires and the aggressive tier spent every game razing a
  // base one building at a time with one blob.
  const mem = rememberedStructures(world, ai);
  if (!mem.length) return false;
  let onSite = 0;
  for (const u of field) {
    for (const m of mem) {
      if (Math.hypot(m.x - u.x, m.y - u.y) < 11) { onSite++; break; }
    }
    if (onSite >= 3) return true;
  }
  return false;
}

function rememberedEnemyStructure(world, ai) {
  const foe = 1 - ai.player;
  for (const m of world.fog[ai.player].memory.values()) if (m.owner === foe) return true;
  return false;
}

function rememberedStructures(world, ai) {
  const foe = 1 - ai.player;
  const out = [];
  for (const [id, m] of world.fog[ai.player].memory) if (m.owner === foe) out.push({ id, x: m.x, y: m.y, defId: m.defId });
  return out;
}

/**
 * The nearest remembered building no other party has taken. Town halls and
 * production first, exactly like pickTarget: they are what the enemy rebuilds
 * from, and a depot is only ever the last thing left standing.
 */
function claimRazeTarget(list, at, taken) {
  let best = null, bs = Infinity;
  for (const m of list.values()) {
    if (taken.has(m.id)) continue;
    const def = BuildingDefs[m.defId];
    const weight = def.isTownHall ? -8 : def.produces.length ? -4 : 0;
    const d = Math.hypot(m.x - at.x, m.y - at.y) + weight;
    if (d < bs) { bs = d; best = m; }
  }
  return best;
}

/** Put the hunting workers back on minerals and forget the parties. */
function endHunt(world, ai) {
  const h = ai.hunt;
  h.parties.length = 0;
  if (h.scouts.size) {
    const ids = [...h.scouts].filter((id) => world.alive(id));
    h.scouts.clear();
    if (ids.length) world.issue({ type: 'gather', player: ai.player, ids, targetId: null });
  }
}

function doHunt(world, ai, field) {
  const sq = ai.squad;
  if (sq.state !== 'hunt') { setState(world, sq, 'hunt'); ai.hunt.parties.length = 0; }
  sq.ids = new Set(field.map((u) => u.id));
  sq.sieging = false;
  sq.threat = null;
  ai.holdPoint = null;
  ai.targetKind = 'sweep';

  const s = sweepState(world, ai);
  markSweepSeen(world, ai, s);
  // Nothing left unsearched and the enemy is still alive: he has rebuilt behind
  // us. Start the pass again rather than standing still.
  if (!s.cells.some((c, i) => c && !s.visited[i])) {
    s.visited.fill(0); s.pass++;
    markSweepSeen(world, ai, s);
    for (const P of ai.hunt.parties) P.idx = -1;
  }

  const parties = huntParties(world, ai, field, s);
  const byId = new Map(field.map((u) => [u.id, u]));

  // Nothing to set up against out here, and a packed tank is a tank that walks.
  for (const u of field) {
    if (u.sieged && u.siegeTimer <= 0) world.issue({ type: 'unsiege', player: ai.player, ids: [u.id] });
  }

  const spots = parties.map((P) => partyCentre(P, byId));
  const claimed = new Set();
  const razeById = new Map();
  for (const m of rememberedStructures(world, ai)) razeById.set(m.id, m);
  const taken = new Set();

  // ---- who is going where ------------------------------------------------
  // ONE party per objective. Without the claim every party in the base picks
  // the same nearest building, five groups of four queue up on one depot, and
  // the split has bought nothing at all -- which is exactly what the first cut
  // of this did on seed 17: five parties, two distinct targets, and a hundred
  // and thirty seconds to knock over fifteen buildings.
  //
  // A party keeps the building it was sent to until that building is gone, so
  // an objective does not swap hands every time a centroid drifts a tile.
  for (const P of parties) {
    if (P.raze != null && !razeById.has(P.raze)) P.raze = null;
    if (P.raze != null) taken.add(P.raze);
  }
  // Anything we can SEE outranks everything on every list, at ANY distance,
  // and the nearest party goes and kills it. Distance-limiting that to "chase
  // what happens to be near you" is a bug with a plausible face on it: on seed
  // 4 a scouting SCV stood four tiles from the last surviving worker, lit it
  // up, struck the cell off as searched -- and every party was thirty tiles
  // away, so nobody went. The sweep then went round the whole map eleven more
  // times with the survivor in plain sight. One party per target; the rest
  // carry on searching.
  const busy = new Set();
  const hunted = new Set();
  // A party that has been sent after something keeps going after it, including
  // for a few seconds after it walks back into the fog -- to its LAST KNOWN
  // position, which is what a player does and what the AI's own fog supports.
  // Dropping the chase the instant vision is lost turns a runner into a
  // permanent stalemate: seen, forgotten, seen again, forgotten again.
  for (let i = 0; i < parties.length; i++) {
    const P = parties[i];
    const e = P.prey != null ? world.alive(P.prey) : null;
    if (!e) { P.prey = null; continue; }
    if (canSee(world, ai.player, e)) { P.preyAt = { x: e.x, y: e.y }; P.preyTick = world.tick; }
    else if (world.tick - (P.preyTick ?? -1e6) > TICK_RATE * 15) { P.prey = null; continue; }
    busy.add(i); hunted.add(P.prey);
  }
  for (const e of world.unitsOf(1 - ai.player)) {
    if (hunted.has(e.id) || !canSee(world, ai.player, e)) continue;
    let best = -1, bd = Infinity;
    for (let i = 0; i < parties.length; i++) {
      if (busy.has(i) || !parties[i].ids.size) continue;
      const d = Math.hypot(spots[i].x - e.x, spots[i].y - e.y);
      if (d < bd) { bd = d; best = i; }
    }
    if (best < 0) break;
    busy.add(best);
    hunted.add(e.id);
    if (parties[best].raze != null) taken.delete(parties[best].raze);
    parties[best].prey = e.id;
    parties[best].preyAt = { x: e.x, y: e.y };
    parties[best].preyTick = world.tick;
    parties[best].raze = null;
  }
  for (let i = 0; i < parties.length; i++) {
    const P = parties[i];
    if (!P.ids.size || P.prey != null) continue;
    if (P.raze == null) {
      const m = claimRazeTarget(razeById, spots[i], taken);
      if (m) { P.raze = m.id; taken.add(m.id); }
    }
  }

  // ---- and the orders ----------------------------------------------------
  for (let i = 0; i < parties.length; i++) {
    const P = parties[i];
    const units = [];
    for (const id of P.ids) { const u = byId.get(id); if (u) units.push(u); }
    if (!units.length) continue;
    const at = spots[i];
    const prey = P.prey != null ? world.alive(P.prey) : null;
    const raze = P.raze != null ? razeById.get(P.raze) : null;

    let goal;
    if (prey) {
      P.idx = -1;
      const to = canSee(world, ai.player, prey) ? prey : (P.preyAt || prey);
      goal = stablePoint(ai, `hunt${i}`, to.x, to.y, 2.5);
    } else if (raze) {
      P.idx = -1;
      goal = stablePoint(ai, `hunt${i}`, raze.x, raze.y, 1.5);
    } else {
      if (P.idx < 0 || !s.cells[P.idx] || s.visited[P.idx] || world.tick - P.since > HUNT_GIVEUP) {
        if (P.idx >= 0 && !s.visited[P.idx] && world.tick - P.since > HUNT_GIVEUP) s.visited[P.idx] = 1;
        const others = spots.filter((_, j) => j !== i);
        P.idx = claimHuntCell(s, at, others, claimed);
        P.since = world.tick;
        clearPoint(ai, `hunt${i}`);
      }
      const c = P.idx >= 0 ? s.cells[P.idx] : { x: ai.enemyHome.x, y: ai.enemyHome.y };
      if (P.idx >= 0) claimed.add(P.idx);
      goal = stablePoint(ai, `hunt${i}`, c.x, c.y, 0);
    }
    P.pt = goal;
    issueGroup(world, ai, 'attackmove', units.filter((u) => !u.sieged && u.siegeTimer <= 0), goal.x, goal.y);
  }

  // Diagnostics and the rest of the file both read squad.target; point it at a
  // party that is on something real, or at the first one that is searching.
  const lead = parties.find((P) => P.pt && (P.prey != null || P.raze != null)) || parties.find((P) => P.pt);
  if (lead) sq.target = { x: lead.pt.x, y: lead.pt.y };
  huntScouts(world, ai, s, claimed, spots);
  const st = stagingPoint(world, ai);
  setRally(world, ai, st.x, st.y);
}

function partyCentre(P, byId) {
  let sx = 0, sy = 0, n = 0;
  for (const id of P.ids) { const u = byId.get(id); if (u) { sx += u.x; sy += u.y; n++; } }
  return n ? { x: sx / n, y: sy / n } : { x: 0, y: 0 };
}

/**
 * Keep the parties. Reform only when the shape of the army has actually changed
 * -- reforming every decision tick would re-order every unit four times a
 * second and the search would never travel anywhere.
 */
function huntParties(world, ai, field, s) {
  const h = ai.hunt;
  const live = new Set(field.map((u) => u.id));
  for (const P of h.parties) for (const id of [...P.ids]) if (!live.has(id)) P.ids.delete(id);
  h.parties = h.parties.filter((P) => P.ids.size);

  const want = Math.max(1, Math.min(ai.cfg.huntParties, Math.floor(field.length / HUNT_PARTY_MIN)));
  if (h.parties.length !== want) return formParties(world, ai, field, s, want);

  // Reinforcements: fold each new unit into the party it is nearest to rather
  // than re-cutting the whole army.
  const seen = new Set();
  for (const P of h.parties) for (const id of P.ids) seen.add(id);
  const byId = new Map(field.map((u) => [u.id, u]));
  const spots = h.parties.map((P) => partyCentre(P, byId));
  for (const u of field) {
    if (seen.has(u.id)) continue;
    let best = 0, bd = Infinity;
    for (let i = 0; i < spots.length; i++) {
      const d = Math.hypot(u.x - spots[i].x, u.y - spots[i].y);
      if (d < bd) { bd = d; best = i; }
    }
    h.parties[best].ids.add(u.id);
  }
  return h.parties;
}

function formParties(world, ai, field, s, want) {
  const h = ai.hunt;
  const at = { x: 0, y: 0 };
  for (const u of field) { at.x += u.x / field.length; at.y += u.y / field.length; }
  // Seed on the buildings we are here to knock down, spread as widely as they
  // allow, and fill any remaining party with a corner of the map to search.
  const seeds = razeSeeds(rememberedStructures(world, ai), want, at);
  if (seeds.length < want) for (const sd of huntSeeds(s, want - seeds.length, at)) seeds.push(sd);
  h.parties = [];
  const pool = [...field];
  const n = seeds.length || 1;
  for (let i = 0; i < n; i++) {
    const seed = seeds[i] || null;
    const take = Math.ceil(pool.length / (n - i));
    // Nearest units to this seed, so a party is a group of neighbours rather
    // than a list of unit ids drawn from all over the map.
    if (seed) pool.sort((a, b) => Math.hypot(a.x - seed.pt.x, a.y - seed.pt.y) - Math.hypot(b.x - seed.pt.x, b.y - seed.pt.y));
    const ids = new Set(pool.splice(0, take).map((u) => u.id));
    h.parties.push({ ids, idx: seed ? seed.i : -1, since: world.tick, pt: null, raze: null, prey: null, preyAt: null, preyTick: -1e6 });
  }
  return h.parties;
}

/**
 * `k` unsearched cells to start from, chosen to be a long way apart. Base sites
 * and mineral lines (the `hot` cells) come first because that is where a
 * survivor actually is; after that it is a trade between how far a party has to
 * walk and how much new ground the split buys.
 */
/** Up to `k` remembered buildings, chosen to be as far apart as they get. */
function razeSeeds(list, k, from) {
  const chosen = [];
  for (let n = 0; n < k && chosen.length < list.length; n++) {
    let best = null, bs = -Infinity;
    for (const m of list) {
      if (chosen.some((c) => c.m === m)) continue;
      let sep = Infinity;
      for (const c of chosen) sep = Math.min(sep, Math.hypot(c.m.x - m.x, c.m.y - m.y));
      const score = Math.min(sep, 30) * 1.2 - Math.hypot(m.x - from.x, m.y - from.y) * 0.4;
      if (score > bs) { bs = score; best = m; }
    }
    if (!best) break;
    chosen.push({ m: best, i: -1, pt: { x: best.x, y: best.y } });
  }
  return chosen;
}

function huntSeeds(s, k, from) {
  const chosen = [];
  for (let n = 0; n < k; n++) {
    let best = -1, bs = -Infinity;
    for (let i = 0; i < s.cells.length; i++) {
      const c = s.cells[i];
      if (!c || s.visited[i] || chosen.includes(i)) continue;
      let sep = Infinity;
      for (const j of chosen) sep = Math.min(sep, Math.hypot(c.x - s.cells[j].x, c.y - s.cells[j].y));
      const score = (s.hot[i] ? 26 : 0) + Math.min(sep, 40) * 1.25 - Math.hypot(c.x - from.x, c.y - from.y) * 0.5;
      if (score > bs) { bs = score; best = i; }
    }
    if (best < 0) break;
    chosen.push(best);
  }
  return chosen.map((i) => ({ i, pt: s.cells[i] }));
}

/**
 * The nearest unsearched cell to this party that is not already somebody's,
 * hot cells first. Cells that are much closer to another party are skipped --
 * that is their ground, and two parties walking through each other search half
 * as much map as two parties that do not.
 */
function claimHuntCell(s, at, others, claimed) {
  for (let pass = 0; pass < 2; pass++) {
    let best = -1, bs = Infinity, bestHot = false;
    for (let i = 0; i < s.cells.length; i++) {
      const c = s.cells[i];
      if (!c || s.visited[i] || claimed.has(i)) continue;
      const d = Math.hypot(c.x - at.x, c.y - at.y);
      if (pass === 0) {
        let dOther = Infinity;
        for (const o of others) dOther = Math.min(dOther, Math.hypot(c.x - o.x, c.y - o.y));
        if (dOther + 6 < d) continue;
      }
      const hot = s.hot[i] === 1;
      if (bestHot && !hot) continue;
      if (hot && !bestHot) { bestHot = true; bs = d; best = i; continue; }
      if (d < bs) { bs = d; best = i; }
    }
    if (best >= 0) return best;
  }
  return -1;
}

/**
 * Two or three workers sent to look at the far side of the map. A worker walks
 * faster than a rifleman, the game is already decided, and this is exactly what
 * the spare SCVs are for -- but they are sent with a MOVE order, not an attack:
 * they are eyes, and a worker that stops to punch a supply depot is neither.
 */
function huntScouts(world, ai, s, claimed, spots) {
  const h = ai.hunt;
  const want = ai.cfg.huntScouts;
  for (const id of [...h.scouts]) if (!world.alive(id)) h.scouts.delete(id);
  if (!want) return;
  if (h.scouts.size < want && ai.emergency === 0) {
    for (const u of world.unitsOf(ai.player)) {
      if (h.scouts.size >= want) break;
      if (!UnitDefs[u.defId].isWorker) continue;
      if (u.id === ai.scoutId || ai.defenders.has(u.id) || h.scouts.has(u.id)) continue;
      if (u.order && u.order.type === 'build') continue;
      h.scouts.add(u.id);
    }
  }
  let i = 0;
  for (const id of h.scouts) {
    const u = world.alive(id);
    if (!u) continue;
    const key = `hscout${i++}`;
    const cur = ai.gp[key];
    // Keep walking until the cell has actually been looked at.
    if (cur && !cellSeen(world, ai, cur)) continue;
    const idx = claimHuntCell(s, u, spots, claimed);
    if (idx < 0) continue;
    claimed.add(idx);
    const c = s.cells[idx];
    clearPoint(ai, key);
    const pt = stablePoint(ai, key, c.x, c.y, 0);
    world.issue({ type: 'move', player: ai.player, ids: [u.id], x: pt.x, y: pt.y });
  }
}

function cellSeen(world, ai, pt) {
  const g = world.grid;
  return world.fog[ai.player].vis[Math.floor(pt.y) * g.w + Math.floor(pt.x)] === FOG_VISIBLE;
}

/**
 * A point `dist` tiles from `target` on the side the army is already on, snapped
 * to walkable ground. This is where the tanks set up.
 */
function siegeStandoff(world, target, anchor, dist) {
  const dx = anchor.x - target.x, dy = anchor.y - target.y;
  const l = Math.hypot(dx, dy) || 1;
  const s = world.grid.nearestPassable(target.x + (dx / l) * dist, target.y + (dy / l) * dist);
  return { x: s.x + 0.5, y: s.y + 0.5 };
}

function visibleEnemyArmyNear(world, ai, at, r) {
  const foe = 1 - ai.player;
  let v = 0;
  for (const u of world.unitsOf(foe)) {
    const d = UnitDefs[u.defId];
    if (d.isWorker) continue;
    if (!canSee(world, ai.player, u)) continue;
    if (Math.hypot(u.x - at.x, u.y - at.y) > r) continue;
    v += valueOf(u);
  }
  return v;
}

function doRetreat(world, ai, all) {
  const sq = ai.squad;
  const st = stagingPoint(world, ai);
  const pt = stablePoint(ai, 'retreat', st.x, st.y, 3);
  unsiegeFarFrom(world, ai, all, pt, 0);
  const movers = all.filter((u) => !u.sieged && u.siegeTimer <= 0);
  issueGroup(world, ai, 'move', movers, pt.x, pt.y);
  setRally(world, ai, pt.x, pt.y);
  const home = movers.filter((u) => Math.hypot(u.x - pt.x, u.y - pt.y) < 12).length;
  const done = (movers.length && home >= Math.ceil(movers.length * 0.75))
    || world.tick - sq.stateSince > 24 * ai.cfg.regroupSeconds;
  if (done) { sq.ids = new Set(all.map((u) => u.id)); setState(world, sq, 'mass'); }
}

function squadAnchor(world, ai, all) {
  const sq = ai.squad;
  let n = 0, sx = 0, sy = 0;
  for (const u of all) if (sq.ids.has(u.id)) { sx += u.x; sy += u.y; n++; }
  if (!n) return stagingPoint(world, ai);
  return { x: sx / n, y: sy / n };
}

/**
 * Issue one command for a whole group, exactly like a player box-selecting and
 * right-clicking. Units that already have this order to roughly this point are
 * left alone -- re-issuing wipes their path every tick and is what makes a
 * scripted army walk like it is drunk.
 */
function issueGroup(world, ai, type, units, x, y, force = false) {
  const ids = [];
  for (const u of units) {
    if (!force) {
      const o = u.order;
      if (o && o.type === type && Math.hypot((o.x ?? 1e9) - x, (o.y ?? 1e9) - y) < 2.0) continue;
      if (!o && Math.hypot(u.x - x, u.y - y) < 3.5) continue;   // already there and idle
    }
    ids.push(u.id);
  }
  if (!ids.length) return;
  world.issue({ type, player: ai.player, ids, x, y });
}

function setRally(world, ai, x, y) {
  if (world.tick % (AI_PERIOD * 6) !== 0) return;
  const ids = [];
  for (const b of world.buildingsOf(ai.player)) {
    const def = BuildingDefs[b.defId];
    if (b.constructing || def.isTownHall || !def.produces.length) continue;
    if (b.rally && Math.hypot(b.rally.x - x, b.rally.y - y) < 3) continue;
    ids.push(b.id);
  }
  if (ids.length) world.issue({ type: 'rally', player: ai.player, ids, x, y });
}

/**
 * Target selection from remembered structures only. Production and town halls
 * outrank depots, and closer outranks further, so the AI naturally rolls the
 * natural before the main instead of teleporting its aggression around.
 *
 * `from` is where the army actually is; only the sweep uses it, because a
 * search should start from the searcher and everything else should stay
 * anchored on home so the objective does not teleport around the map.
 */
function pickTarget(world, ai, from = null) {
  const foe = 1 - ai.player;
  let best = null, bs = Infinity;
  for (const m of world.fog[ai.player].memory.values()) {
    if (m.owner !== foe) continue;
    const def = BuildingDefs[m.defId];
    // A player who knows the matchup goes for the town hall and the production
    // and leaves the depots for last. One who does not walks at whatever is
    // nearest -- which is usually a depot on the near edge of the base, and is
    // most of the difference between a push that kills a base and one that
    // knocks a hole in the wall and then dies to what came out of it.
    const weight = ai.cfg.smartTargets ? (def.isTownHall ? -6 : def.produces.length ? 0 : 10) : 0;
    const d = Math.hypot(m.x - ai.home.x, m.y - ai.home.y) + weight;
    if (d < bs) { bs = d; best = m; }
  }
  if (best) { ai.targetKind = 'memory'; return { x: best.x, y: best.y }; }

  // Nothing remembered standing: go for anything we can actually see.
  let u = null, ud = Infinity;
  for (const e of world.unitsOf(foe)) {
    if (!canSee(world, ai.player, e)) continue;
    const d = Math.hypot(e.x - ai.home.x, e.y - ai.home.y);
    if (d < ud) { ud = d; u = e; }
  }
  if (u) { ai.targetKind = 'sighted'; return { x: Math.floor(u.x) + 0.5, y: Math.floor(u.y) + 0.5 }; }

  // And if there is nothing to see either, SWEEP. A won game that ends with the
  // AI standing in a flattened enemy base while one surviving worker sits in a
  // corner is not a won game -- it is a fifteen-minute draw, and the fix is the
  // same thing a human does: fan out across the black parts of the minimap
  // until the victory banner shows up.
  ai.targetKind = 'sweep';
  return sweepTarget(world, ai, from || ai.staging);
}

// ------------------------------------------------------------- map sweep --
/**
 * Finishing a won game.
 *
 * The old version of this was a fixed list of nine or ten interesting points --
 * the base locations, the map centre, the four quadrant marks -- cycled on a
 * thirty-second timer. It reads sensible and it does not work: a depot in a
 * corner is twenty tiles from the nearest point on that list, a marine sees
 * seven, and the game hangs forever with a maxed army standing on a tour of
 * places the enemy is not. (StarcraftRTS seeds 27 and 42: 25:00, AI 119/0 up,
 * one surviving human entity, nobody ever looks at it.)
 *
 * So the search is over the map itself, on a coarse grid, driven by the AI's
 * OWN fog -- exactly the black/grey/lit minimap a human is looking at, never
 * the enemy's entity list:
 *
 *   * every cell the army can currently SEE is struck off, whether it walked
 *     there or not. This is what makes a 120-unit blob a broom instead of a
 *     man following a list.
 *   * the base locations and their mineral lines are searched FIRST. That is
 *     not decoration: a surviving worker is standing on a patch, and a patch is
 *     exactly far enough from the town hall that an army parked on the hall's
 *     rubble cannot see one. Sweeping the map in raw distance order walks away
 *     from four live workers standing ten tiles away and finds them again four
 *     minutes later, which is how killing a do-nothing opponent went from 5:28
 *     to 10:01.
 *   * after that, nearest-unsearched-first from where the army actually stands,
 *     so the sweep floods outward instead of criss-crossing the map.
 *   * when every cell has been searched and the enemy is still alive he has
 *     rebuilt behind us, so the pass simply starts again.
 */
const SWEEP_STEP = 5;             // search cell, tiles -- under a marine's sight of 7
const SWEEP_GIVEUP = TICK_RATE * 40;  // abandon a cell we cannot seem to reach
const SWEEP_HOT = 10;             // tiles around a base/mineral line that get searched first

function sweepState(world, ai) {
  if (ai.sweep) return ai.sweep;
  const g = world.grid;
  const cw = Math.ceil(g.w / SWEEP_STEP), ch = Math.ceil(g.h / SWEEP_STEP);

  // Places a survivor is actually likely to be: every base's town hall site,
  // every base's mineral line, and the four corners -- which is where a worker
  // that ran actually stops, and where the depot nobody ever scouted is.
  const likely = [];
  for (const [cx, cy] of [[5, 5], [g.w - 5, 5], [5, g.h - 5], [g.w - 5, g.h - 5]]) {
    const c = g.nearestPassable(cx, cy, 10);
    if (g.passable(c.x, c.y)) likely.push({ x: c.x + 0.5, y: c.y + 0.5 });
  }
  for (const b of world.bases) {
    likely.push({ x: b.cc.x, y: b.cc.y });
    let sx = 0, sy = 0, n = 0;
    for (const id of world.resources) {
      const r = world.get(id);
      if (!r || r.base !== b.index || r.defId !== 'mineral') continue;
      sx += r.x; sy += r.y; n++;
    }
    if (n) likely.push({ x: sx / n, y: sy / n });
  }

  const cells = [], hot = new Uint8Array(cw * ch);
  for (let cy = 0; cy < ch; cy++) {
    for (let cx = 0; cx < cw; cx++) {
      const i = cy * cw + cx;
      const s = g.nearestPassable(cx * SWEEP_STEP + SWEEP_STEP / 2, cy * SWEEP_STEP + SWEEP_STEP / 2, 2);
      // nearestPassable hands back the input when it finds nothing inside maxR,
      // so re-test it: a cell that is solid cliff is not a place to go and look.
      const ok = g.passable(s.x, s.y);
      cells.push(ok ? { x: s.x + 0.5, y: s.y + 0.5 } : null);
      if (ok) for (const l of likely) {
        if (Math.hypot(s.x + 0.5 - l.x, s.y + 0.5 - l.y) <= SWEEP_HOT) { hot[i] = 1; break; }
      }
    }
  }
  ai.sweep = { cells, hot, visited: new Uint8Array(cw * ch), idx: -1, pt: null, since: -1e6, pass: 0 };
  return ai.sweep;
}

/**
 * Strike off every cell we have actually SEARCHED.
 *
 * "Searched" is not "its centre tile is lit". A rifleman sees 7 and a cell is
 * five tiles across, so a unit that lights a cell's centre from six tiles away
 * has seen precisely none of that cell's far half -- and a worker standing in
 * the far half is invisible while the cell is struck off the list. With one
 * blob crawling the map that error is invisible, because the blob walks onto
 * every cell it clears. With six parties criss-crossing it, cells get cleared
 * in PASSING by the thousand: on seed 4 the search finished eleven full passes
 * of the map, having never once looked at the corner the last worker was
 * standing in, and the game timed out at 20:00 with one entity left.
 *
 * So a cell only counts once somebody is inside it -- within its own half
 * diagonal of the point, which is the distance at which the whole cell is
 * genuinely inside that unit's sight. The vision test stays on top of that, so
 * the height rule still applies: standing below a cliff does not search the
 * plateau above it.
 */
const SWEEP_COVER = SWEEP_STEP * 0.71;   // half a cell's diagonal

function markSweepSeen(world, ai, s) {
  const g = world.grid, vis = world.fog[ai.player].vis;
  const cw = Math.ceil(g.w / SWEEP_STEP), ch = Math.ceil(g.h / SWEEP_STEP);
  const mark = (x, y, sight) => {
    const r = (sight || 7) - SWEEP_COVER;
    if (r <= 0) return;
    const x0 = Math.max(0, ((x - r) / SWEEP_STEP) | 0), x1 = Math.min(cw - 1, ((x + r) / SWEEP_STEP) | 0);
    const y0 = Math.max(0, ((y - r) / SWEEP_STEP) | 0), y1 = Math.min(ch - 1, ((y + r) / SWEEP_STEP) | 0);
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        const i = cy * cw + cx, c = s.cells[i];
        if (!c || s.visited[i]) continue;
        if (Math.hypot(c.x - x, c.y - y) > r) continue;
        if (vis[Math.floor(c.y) * g.w + Math.floor(c.x)] !== FOG_VISIBLE) continue;
        s.visited[i] = 1;
      }
    }
  };
  for (const u of world.unitsOf(ai.player)) mark(u.x, u.y, u.sight);
  for (const b of world.buildingsOf(ai.player)) if (!b.constructing) mark(b.x, b.y, b.sight);
}

/**
 * Nearest unsearched cell to `from`; base and mineral-line cells outrank
 * everything else. -1 when there is nothing left unsearched.
 */
function nextSweepCell(s, from) {
  let best = -1, bs = Infinity, bestHot = false;
  for (let i = 0; i < s.cells.length; i++) {
    const c = s.cells[i];
    if (!c || s.visited[i]) continue;
    const isHot = s.hot[i] === 1;
    if (bestHot && !isHot) continue;
    const d = Math.hypot(c.x - from.x, c.y - from.y);
    if (isHot && !bestHot) { bs = d; best = i; bestHot = true; continue; }
    if (d < bs) { bs = d; best = i; }
  }
  return best;
}

function sweepTarget(world, ai, from) {
  const s = sweepState(world, ai);
  markSweepSeen(world, ai, s);

  // Still walking to a cell we have not managed to look at yet: keep going.
  if (s.idx >= 0 && !s.visited[s.idx] && world.tick - s.since <= SWEEP_GIVEUP) return s.pt;
  // Gave up on the clock -- unreachable, or something is in the way. Call it
  // searched, or the sweep deadlocks on one cell for the rest of the game.
  if (s.idx >= 0) s.visited[s.idx] = 1;

  let i = nextSweepCell(s, from);
  if (i < 0) {
    s.visited.fill(0);
    s.pass++;
    markSweepSeen(world, ai, s);
    i = nextSweepCell(s, from);
  }
  if (i < 0) { s.idx = -1; s.since = world.tick; s.pt = { x: ai.enemyHome.x, y: ai.enemyHome.y }; return s.pt; }
  s.idx = i;
  s.pt = { x: s.cells[i].x, y: s.cells[i].y };
  s.since = world.tick;
  return s.pt;
}

/** Do we know of ANY enemy right now -- remembered structure or unit in sight? */
function knowOfEnemy(world, ai) {
  const foe = 1 - ai.player;
  for (const m of world.fog[ai.player].memory.values()) if (m.owner === foe) return true;
  for (const e of world.unitsOf(foe)) if (canSee(world, ai.player, e)) return true;
  return false;
}

/** Anything of the enemy's -- unit or structure -- visible within `r` of a point. */
function anyEnemyVisibleNear(world, ai, at, r) {
  const foe = 1 - ai.player;
  for (const id of world.byPlayer[foe]) {
    const e = world.entities.get(id);
    if (!e || !e.alive) continue;
    if (Math.hypot(e.x - at.x, e.y - at.y) > r) continue;
    if (e.kind === 'building' ? isVisible(world, ai.player, e.x, e.y) : canSee(world, ai.player, e)) return true;
  }
  return false;
}

// ---------------------------------------------------------------- routing --
/**
 * Waypoints from A to B along walkable terrain. A BFS over terrain only (not
 * building footprints) keeps the route stable while bases grow and shrink, and
 * the units' own flow-field pathing handles the fine detail between hops.
 */
function buildRoute(world, ai, from, to) {
  const g = world.grid;
  const key = `${Math.round(to.x / 4)},${Math.round(to.y / 4)}`;
  let dist = ai.routeCache.get(key);
  if (!dist) {
    dist = bfsTerrain(g, to.x, to.y);
    if (ai.routeCache.size > 8) ai.routeCache.clear();
    ai.routeCache.set(key, dist);
  }
  const w = g.w, h = g.h;
  const start = nearestTerrainTile(g, from.x, from.y, dist);
  const out = [];
  if (start < 0 || dist[start] < 0) {
    // no route known -- fall back to a straight line, movement will cope
    const steps = 3;
    for (let i = 1; i <= steps; i++) {
      const f = i / steps;
      out.push({ x: from.x + (to.x - from.x) * f, y: from.y + (to.y - from.y) * f });
    }
    return out;
  }
  let cur = start, guard = 0, since = 0;
  const HOP = 20;
  while (dist[cur] > 12 && guard++ < 4000) {
    const cx = cur % w, cy = (cur / w) | 0;
    let nxt = -1, nd = dist[cur];
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const x = cx + dx, y = cy + dy;
        if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) continue;
        const i = y * w + x;
        if (dist[i] < 0) continue;
        if (dist[i] < nd) { nd = dist[i]; nxt = i; }
      }
    }
    if (nxt < 0) break;
    cur = nxt;
    if (++since >= HOP) {
      since = 0;
      const px = cur % w, py = (cur / w) | 0;
      const s = g.nearestPassable(px, py);
      out.push({ x: s.x + 0.5, y: s.y + 0.5 });
    }
  }
  // final approach: hold up ~12 tiles short, then commit onto the target
  const cx = cur % w, cy = (cur / w) | 0;
  const s = g.nearestPassable(cx, cy);
  const staging = { x: s.x + 0.5, y: s.y + 0.5 };
  if (!out.length || Math.hypot(out[out.length - 1].x - staging.x, out[out.length - 1].y - staging.y) > 6) out.push(staging);
  out.push({ x: to.x, y: to.y });
  return out;
}

function bfsTerrain(g, tx, ty) {
  const w = g.w, h = g.h, n = w * h;
  const dist = new Int32Array(n).fill(-1);
  const q = new Int32Array(n);
  let qh = 0, qt = 0;
  const s = g.nearestPassable(tx, ty);
  const si = (s.y | 0) * w + (s.x | 0);
  dist[si] = 0; q[qt++] = si;
  while (qh < qt) {
    const cur = q[qh++];
    const cx = cur % w, cy = (cur / w) | 0;
    const cd = dist[cur] + 1;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const x = cx + dx, y = cy + dy;
        if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) continue;
        const i = y * w + x;
        if (dist[i] >= 0 || g.cliff[i] !== 0) continue;
        dist[i] = cd; q[qt++] = i;
      }
    }
  }
  return dist;
}

function nearestTerrainTile(g, x, y, dist) {
  const w = g.w;
  let bx = Math.max(1, Math.min(g.w - 2, Math.round(x)));
  let by = Math.max(1, Math.min(g.h - 2, Math.round(y)));
  if (dist[by * w + bx] >= 0) return by * w + bx;
  for (let r = 1; r <= 12; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const nx = bx + dx, ny = by + dy;
        if (nx < 1 || ny < 1 || nx >= g.w - 1 || ny >= g.h - 1) continue;
        if (dist[ny * w + nx] >= 0) return ny * w + nx;
      }
    }
  }
  return -1;
}

// ------------------------------------------------------------ siege micro --
/**
 * Siege mode is only worth its 2 s transition when there is something to shoot
 * at from OUTSIDE the range of whatever is shooting back. Riflemen reach 4.5,
 * missile turrets 7, an unsieged tank 7; a sieged tank reaches 12 with a 2.0
 * minimum. So the set-up band is 7.5 to 11.5 tiles from the nearest thing we
 * can see: far enough that the defender has to walk out of its own position to
 * answer, close enough that the shells land.
 *
 * Sieging mid-field on a wandering scout is worse than not sieging at all --
 * it takes two seconds to unpack, and the army walks off without it -- so a
 * lone unit is not a reason to set up. A defended POSITION is: a structure, or
 * two or more units holding ground.
 */
function tankMicro(world, ai, p) {
  if (!p.tech.has('siege_tech')) return;
  const sq = ai.squad;
  const fighting = sq.state === 'attack' || sq.state === 'defend';

  // A tank that only ever sets up on what it can SEE inside 6.5-11.8 tiles is a
  // tank that mostly does not set up at all: that band is five tiles wide, the
  // enemy crosses it in under two seconds and the deployment takes two. The two
  // cases below are the ones where the ground is known in advance, which is the
  // only way a tank ever gets to fight the fight it was built for.
  const hold = ai.holdPoint;
  // A position is worth committing to while there is something to hold it
  // against. Twenty seconds of grace after the last contact, because the
  // attacker stepping out of vision is not the attacker going home -- but not
  // forever, or the tanks are still dug in at the ramp when the army leaves.
  const expecting = !!sq.threat || world.tick - ai.lastThreatTick < TICK_RATE * 20;
  const dugIn = hold && expecting && (sq.state === 'defend' || sq.state === 'mass');
  // The deliberate stand-off on the attack: the objective is a REMEMBERED
  // building, usually not currently visible, so "nearest visible target" never
  // fires and the tanks walk the last ten tiles in mobile mode.
  const standoff = sq.state === 'attack' && sq.sieging && sq.target ? sq.target : null;

  for (const u of world.unitsOf(ai.player)) {
    if (!UnitDefs[u.defId].canSiege) continue;
    if (u.siegeTimer > 0) continue;
    const tgt = nearestVisibleGroundTarget(world, ai, u.x, u.y, 14);
    const d = tgt ? Math.hypot(tgt.x - u.x, tgt.y - u.y) : Infinity;
    const atHold = dugIn && Math.hypot(u.x - hold.x, u.y - hold.y) <= 4.5;
    const atStandoff = standoff && shouldSetUp(world, ai, u, standoff);

    if (!u.sieged) {
      if (atHold || atStandoff) {
        // Never unpack on top of them: below 6 tiles a sieged tank is inside
        // its own minimum range for half of them and inside a rifleman's range
        // for all of them, and the two seconds spent deploying are free hits.
        if (nearestVisibleEnemyDistance(world, ai, u.x, u.y, 7) >= 6.5) {
          world.issue({ type: 'siege', player: ai.player, ids: [u.id] });
        }
        continue;
      }
      if (!fighting || !tgt) continue;
      const position = tgt.kind === 'building' || enemyCountNear(world, ai, tgt.x, tgt.y, 7) >= 2;
      const underFire = world.tick - (u.lastDamagedTick ?? -1e6) < 24;
      if (!position && !underFire && !sq.sieging) continue;
      if (d >= 6.5 && d <= 11.8) world.issue({ type: 'siege', player: ai.player, ids: [u.id] });
    } else {
      // Ground we chose is ground we keep. Packing up because nothing is in
      // sight this instant is how a siege line becomes a convoy.
      if ((atHold || atStandoff) && d >= 2.0) continue;
      const anchor = sq.ids.size ? squadAnchor(world, ai, armyUnits(world, ai)) : null;
      const abandoned = anchor && Math.hypot(anchor.x - u.x, anchor.y - u.y) > 16;
      if (!fighting || !tgt || d > 12.5 || d < 2.0 || abandoned) {
        world.issue({ type: 'unsiege', player: ai.player, ids: [u.id] });
      }
    }
  }
}

/** Distance to the nearest enemy of ANY kind we can currently see. */
function nearestVisibleEnemyDistance(world, ai, x, y, r) {
  const foe = 1 - ai.player;
  let best = Infinity;
  for (const id of world.byPlayer[foe]) {
    const e = world.entities.get(id);
    if (!e || !e.alive) continue;
    const d = Math.hypot(e.x - x, e.y - y);
    if (d >= best || d > r) continue;
    if (e.kind === 'building' ? isVisible(world, ai.player, e.x, e.y) : canSee(world, ai.player, e)) best = d;
  }
  return best;
}

function enemyCountNear(world, ai, x, y, r) {
  const foe = 1 - ai.player;
  let n = 0;
  for (const u of world.unitsOf(foe)) {
    if (u.flying) continue;
    if (!canSee(world, ai.player, u)) continue;
    if (Math.hypot(u.x - x, u.y - y) <= r) n++;
  }
  return n;
}

function unsiegeFarFrom(world, ai, all, to, keepWithin) {
  for (const u of all) {
    if (!u.sieged || u.siegeTimer > 0) continue;
    if (keepWithin > 0 && Math.hypot(u.x - to.x, u.y - to.y) <= keepWithin) continue;
    world.issue({ type: 'unsiege', player: ai.player, ids: [u.id] });
  }
}

function nearestVisibleGroundTarget(world, ai, x, y, r) {
  const foe = 1 - ai.player;
  let best = null, bd = r;
  for (const id of world.byPlayer[foe]) {
    const e = world.entities.get(id);
    if (!e || !e.alive || e.flying) continue;
    if (e.kind === 'unit' && !canSee(world, ai.player, e)) continue;
    if (e.kind === 'building' && !isVisible(world, ai.player, e.x, e.y)) continue;
    const d = Math.hypot(e.x - x, e.y - y);
    if (d < bd) { bd = d; best = e; }
  }
  return best;
}

// ------------------------------------------------------------- air harass --
/**
 * Cloaked wraiths into the mineral line. This is the pressure that makes the
 * player's detectors matter; without Cloaking Field the wraiths just reinforce
 * the main army instead.
 */
function wraithHarass(world, ai, p) {
  const h = ai.harass;
  for (const id of [...h.ids]) if (!world.alive(id)) h.ids.delete(id);
  if (!p.tech.has('cloaking_field')) {
    if (h.ids.size) h.ids.clear();
    return;
  }
  const free = world.unitsOf(ai.player).filter((u) => u.defId === 'wraith' && !h.ids.has(u.id));
  for (const u of free) { if (h.ids.size >= 4) break; h.ids.add(u.id); }
  if (h.ids.size < 3 && h.state === 'idle') return;

  const flight = [...h.ids].map((id) => world.alive(id)).filter(Boolean);
  if (!flight.length) { h.state = 'idle'; return; }

  const uncloaked = flight.filter((u) => !u.cloaked);
  if (uncloaked.length) world.issue({ type: 'cloak', player: ai.player, ids: uncloaked.map((u) => u.id) });

  const hurt = flight.some((u) => u.hp < u.maxHp * 0.45);
  if (h.state === 'idle' && flight.length >= 3) { h.state = 'raid'; h.since = world.tick; }
  if (h.state === 'raid' && (hurt || flight.length < 2)) { h.state = 'home'; h.since = world.tick; }
  if (h.state === 'home' && flight.every((u) => u.hp > u.maxHp * 0.85) && flight.length >= 3) { h.state = 'raid'; h.since = world.tick; }

  if (h.state === 'raid') {
    const spot = enemyMineralLine(world, ai);
    const pt = stablePoint(ai, 'harass', spot.x, spot.y, 4);
    issueGroup(world, ai, 'attackmove', flight, pt.x, pt.y);
  } else {
    const st = stagingPoint(world, ai);
    const pt = stablePoint(ai, 'harassHome', st.x, st.y, 3);
    issueGroup(world, ai, 'move', flight, pt.x, pt.y);
  }
}

function enemyMineralLine(world, ai) {
  const foe = 1 - ai.player;
  // Aim at a remembered enemy town hall; the workers are always beside it.
  let best = null, bd = Infinity;
  for (const m of world.fog[ai.player].memory.values()) {
    if (m.owner !== foe || !BuildingDefs[m.defId].isTownHall) continue;
    const d = Math.hypot(m.x - ai.home.x, m.y - ai.home.y);
    if (d < bd) { bd = d; best = m; }
  }
  return best ? { x: best.x, y: best.y } : { x: ai.enemyHome.x, y: ai.enemyHome.y };
}

// ----------------------------------------------------------- worker pull ---
/**
 * The classic response to an early rush that arrives before your army does:
 * pull enough workers to trade, then put them straight back on minerals. It is
 * only ever worth it when the army is not already there.
 */
function workerDefence(world, ai, p, threat) {
  if (!ai.cfg.workerPull) return;
  const release = () => {
    if (!ai.defenders.size) return;
    const ids = [...ai.defenders].filter((id) => world.alive(id));
    ai.defenders.clear();
    if (ids.length) world.issue({ type: 'gather', player: ai.player, ids, targetId: null });
  };
  // Never the last of the economy. Below the emergency line every worker IS the
  // recovery, and one that dies holding a ramp has to be bought again out of an
  // income that no longer exists.
  if (ai.emergency >= 1) { release(); return; }
  if (!threat) { release(); return; }

  const near = armyUnits(world, ai).filter((u) => Math.hypot(u.x - threat.x, u.y - threat.y) < 16);
  const defence = sumValue(near);
  if (defence >= threat.value * 0.9) { release(); return; }
  // Worth doing only while the attack is something workers can actually
  // CHANGE. Eight workers are ~40 dps and 480 hp: decisive against six riflemen
  // in the mineral line, theatre against a thirty-supply timing attack -- and
  // thirty supply does not stop at the eight, it carries on through the other
  // twelve. Past this line the workers are not defenders, they are casualties,
  // and evacuateWorkers() takes over.
  if (threat.value > 16) { release(); return; }

  const want = Math.min(8, Math.ceil((threat.value - defence) * 1.5));
  const workers = world.unitsOf(ai.player)
    .filter((u) => UnitDefs[u.defId].isWorker && u.id !== ai.scoutId)
    .sort((a, b) => Math.hypot(a.x - threat.x, a.y - threat.y) - Math.hypot(b.x - threat.x, b.y - threat.y));

  const ids = [];
  for (const w of workers) {
    if (ai.defenders.size >= want) break;
    if (ai.defenders.has(w.id)) continue;
    if (w.order && w.order.type === 'build') continue;
    ai.defenders.add(w.id); ids.push(w.id);
  }
  const pt = stablePoint(ai, 'pull', threat.x, threat.y, 4);
  const active = [...ai.defenders].map((id) => world.alive(id)).filter(Boolean);
  const toOrder = active.filter((u) => !u.order || u.order.type === 'gather'
    || (u.order.type === 'attackmove' && Math.hypot(u.order.x - pt.x, u.order.y - pt.y) > 4));
  if (toOrder.length) world.issue({ type: 'attackmove', player: ai.player, ids: toOrder.map((u) => u.id), x: pt.x, y: pt.y });
}

/**
 * Get the workers OUT of a base that is being overrun.
 *
 * This is the other half of not pulling workers into a fight that is already
 * lost, and on its own it is worth more than the fight was. Nineteen workers
 * standing on a mineral patch while an army walks through them are nineteen
 * workers that die in forty seconds and take the whole game with them; the same
 * nineteen mining at the other base are an economy that is still running, and
 * an AI that still has a game to play.
 *
 * They are moved by being told to MINE somewhere else, not by being told to
 * run: a fleeing worker that stops working is a worker that has been killed
 * slowly, and an AI that marches its line off to stand behind the army has
 * bought the attacker exactly what the attack was for, for free. So with only
 * one base left this does nothing at all -- there is nowhere to go that still
 * pays -- and the workers keep mining under fire.
 */
function evacuateWorkers(world, ai, all, threat) {
  if (!threat) return;
  if (world.tick - ai.evacTick < TICK_RATE) return;         // one order a second
  const halls = world.buildingsOf(ai.player).filter((b) => BuildingDefs[b.defId].isTownHall && !b.constructing);
  if (!halls.length) return;

  let hit = null, hd = Infinity;
  for (const h of halls) {
    const d = Math.hypot(h.x - threat.x, h.y - threat.y);
    if (d < hd) { hd = d; hit = h; }
  }
  if (hd > 15) return;                                      // not on top of a base
  // Only for an attack that is actually going to take the base. A raid the
  // defence can trade with, or a single flier over the mineral line, is not a
  // reason to stop mining -- and a worker that stops mining because two
  // riflemen walked past has cost more than the two riflemen ever could.
  if (threat.value < 8) return;
  const near = all.filter((u) => Math.hypot(u.x - threat.x, u.y - threat.y) < 16);
  if (sumValue(near) >= threat.value * 0.5) return;         // we can trade here; keep mining

  const flee = [];
  for (const u of world.unitsOf(ai.player)) {
    if (!UnitDefs[u.defId].isWorker) continue;
    if (u.id === ai.scoutId || ai.defenders.has(u.id)) continue;
    if (u.order && u.order.type === 'build') continue;
    if (Math.hypot(u.x - threat.x, u.y - threat.y) > 12) continue;
    flee.push(u);
  }
  if (!flee.length) return;
  ai.evacTick = world.tick;

  // Somewhere to go has to be somewhere that still WORKS. With only one base
  // left there is no such place: marching the line off to stand behind the
  // army turns a base that is being attacked into a base that has stopped
  // mining, which is the same outcome the attack was trying to buy, for free.
  const safe = halls.find((h) => h.id !== hit.id && Math.hypot(h.x - threat.x, h.y - threat.y) > 24);
  if (!safe) return;
  let best = null, bd = Infinity;
  for (const id of world.resources) {
    const r = world.get(id);
    if (!r || !r.alive || r.defId !== 'mineral' || r.amount <= 0) continue;
    const d = Math.hypot(r.x - safe.x, r.y - safe.y);
    if (d < 13 && d < bd) { bd = d; best = r; }
  }
  if (!best) return;
  world.issue({ type: 'gather', player: ai.player, ids: flee.map((u) => u.id), targetId: best.id });
}
